import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
  timeout,
  toArray,
} from 'rxjs/operators';
import {
  CareCallGuideDoc,
  GuideAutoCheckRule,
  GuideEscalation,
  GuideField,
  GuideFormResponse,
  GuidePrimitive,
  GuideRole,
  GuideSection,
  GuideStatusBand,
  GuideSymptomGate,
  GuideVisibilityRule,
  ResolvedFinding,
} from 'src/app/models/care-call-guide.model';
import { GuideCapturedResponse } from 'src/app/models/care-call-guide-captured-response.model';
import { resolveCareCallRole } from 'src/app/care-call-guide/models/care-call-guide-role.util';
import { findProtocolClause, stripSeededSectionPrefix } from 'src/app/care-call-guide/models/care-call-guide-protocol-clauses';
import { findOrphanedScopeReferences, scopeGuideToClinic } from 'src/app/care-call-guide/models/care-call-guide-scope.util';
import { CareCallGuideFormCacheService } from 'src/app/services/care-call-guide/care-call-guide-form-cache.service';
import { ClinicalTask } from 'src/app/models/clients/clinical-task.model';
import { Diagnosis } from 'src/app/models/fhir-models';
import { Patient } from 'src/app/models/patient.model';
import { ProviderLaneEntry } from 'src/app/models/provider-roster.model';
import { patientProgramLabels, resolveMainDiagnosis } from 'src/app/care-call-guide/models/care-call-guide-diagnosis.util';
import {
  legacyNoteSection,
  MAX_HEADER_LENGTH,
  normalizeNoteHeader,
  NOTE_NOT_ADDRESSED_BODY,
  NOTE_UNSTRUCTURED_HEADING,
} from 'src/app/care-call-guide/models/care-call-guide-note-section.util';
import { GenerateNoteService } from 'src/app/services/ai/generate-note.service';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { CareCallGuideService } from 'src/app/services/care-call-guide/care-call-guide.service';
import { DIURETIC_SCREEN_GATE, buildDiureticAttestation } from 'src/app/care-call-guide/models/diuretic-screen.util';
import { ClinicalEligibilityService } from 'src/app/services/clinical-eligibility.service';
import {
  ClinicalEligibilityView,
  EligibilityStatus,
  EXCLUSION_REASON_LABELS,
  mayTitrate,
} from 'src/app/models/clinical-eligibility.model';
import { ClientsService } from 'src/app/services/clients/clients.service';
import { ClinicalNoteCacheService } from 'src/app/services/clinical-note-cache.service';
import { PatientService } from 'src/app/services/patient.service';
import { ProviderService } from 'src/app/services/provider.service';
import { SnackService } from 'src/app/services/snack.service';

/**
 * The fields welby-functions reads off a care-call TASK to mint its audit row (BRD NFR-5.1).
 *
 * THE CONTRACT IS NOT OURS AND MUST NOT BE RENAMED HERE. `recordEscalationEventFromTask`
 * (welby-functions `audit/record-escalation-event.ts`, PR #366) fires on every
 * `clinical_tasks/{taskId}` create and SKIPS any document that carries neither an `escalation_id`
 * nor `source === 'care_call_guide'`. The second is this path's door: with that marker the trigger
 * calls `deriveFromCareCallAlert`, which reads exactly these field names and needs nothing else.
 *
 * Why it is stamped at all: the guide used to write `vital_flags` alerts, and the alert writer
 * (welby-micro-service `CareCallAlertWriterService`) set these names, which is where the trigger's
 * vocabulary comes from. Since this component writes TASKS instead, that endpoint has no caller and
 * NO audit row was being written for a care call at all. Carrying the same names on the task closes
 * that without a change in either of the other two repos, and keeps an audit row and the artefact
 * it came from reading the same way side by side.
 *
 * `source` is NOT `task_source`. That one is the provenance enum the backend defaults to `manual`;
 * this is the audit trigger's discriminant. Both live on the document and neither reads the other.
 *
 * The trigger derives everything else itself: `escalation_id` is `esc_cc_<task doc id>`, the origin
 * is `manual`, the trigger kind is `form_answer`, and the threshold payloads stay null because a
 * guide finding has no rule behind it.
 */
interface CareCallAuditStamp {
  source: 'care_call_guide';
  /** Raw rule owner; the trigger maps a nurse owner to the §4.1 MA-to-NCM handoff event type. */
  owner: string;
  captured_by: string | null;
  guide_diagnosis: string;
  guide_version: string;
  finding_label: string;
  finding_value: string;
  symptomatic?: boolean;
  protocol_clause?: string;
  protocol_clause_text?: string;
  /**
   * When the escalating EVENT happened, where the guide captured it — not when the call was written
   * up. welby-functions anchors §4.2 row 9's deadlines on this and falls back to `create_date`.
   */
  escalation_occurred_at?: Date;
}

/**
 * ONE escalation reason, and therefore ONE task.
 *
 * `key` is `sectionId.fieldId` — the form field that tripped. It is the dedup identity, so a reason
 * whose POST succeeded is never written twice and one whose POST failed is retried on the next
 * Generate Note. `label` becomes the task NAME (see `escalationTaskName`), `body` its description.
 *
 * `audit` is absent on exactly one reason: the aggregate one — see `aggregateEscalationReason`.
 *
 * `priority` is absent on every reason that has no tier to derive it from — the legacy ones and the
 * aggregate one — and `escalationTask` falls back to `'high'` for those, which is what they have
 * always been written with. Only a tiered reason sets it; see `TASK_PRIORITY_BY_TIER`.
 */
interface EscalationReason {
  key: string;
  label: string;
  body: string;
  audit?: CareCallAuditStamp;
  priority?: ClinicalTask['priority'];
}

/**
 * A tripped field on the legacy path, carrying the identity the aggregate line used to discard.
 *
 * The old `legacyEscalationLines` returned bare strings, which was enough while every line landed
 * in one body. A per-reason task needs the field's key to dedup on and its label to be named after.
 */
interface LegacyEscalationReason {
  key: string;
  label: string;
  line: string;
  /**
   * The same finding as a per-reason task BODY — the shape of `findingBlock()`, minus the parts a
   * legacy field genuinely does not have.
   *
   * Two renderings of one finding, on purpose. `line` is the AGGREGATE body's line and is frozen:
   * a clinic outside the canary reads the pre-BRD task, byte for byte. `block` is what the canary's
   * own task carries, and it is the one that had to answer "what changed, what it means, what is
   * recommended" (Marcela Malaver, 2026-09-10) rather than repeating the task's own title.
   */
  block: string[];
  /** The captured ANSWER, for the audit row's `form_answer.answer`. Never the label. */
  value: string;
}

@Component({
  selector: 'app-care-call-guide-base',
  templateUrl: './care-call-guide-base.component.html',
  styleUrls: ['./care-call-guide-base.component.scss'],
})
export class CareCallGuideBaseComponent implements OnInit, OnDestroy {
  private static readonly FORM_CACHE_DEBOUNCE_MS = 300;

  /**
   * Marker sent in place of a guide section's fields when the section was presented to the user
   * but nothing in it was answered. It is what tells the note generator to still print that
   * section (as not addressed) while omitting note sections the guide never asked about.
   */
  private static readonly NOT_ADDRESSED_KEY = 'not_addressed';

  /**
   * `escalation.action` as the NCM should read it. The seed stores the BRD's own vocabulary as a
   * slug; a task body is prose, and 'same_day' in the middle of a sentence reads as a bug.
   * Unmapped values fall through verbatim rather than being dropped — a new action the seed
   * introduces must still reach the NCM, even unprettified.
   */
  private static readonly ACTION_LABELS: Record<string, string> = {
    routine: 'Routine review',
    same_day: 'Same-day provider notification',
    urgent: 'Urgent',
    emergent: 'Emergent',
  };

  /**
   * `escalation.tier` as BRD-CLIN-2026-006 §4.1 words it. The seed stores the tier as a lowercase
   * slug; the protocol's own table says Low / Moderate / High, and the task quotes the protocol.
   *
   * NOT the platform's chip vocabulary, which reads Low / Medium / High — the same distinction
   * `ProtocolClause.tierLabel` documents. An unmapped tier falls through verbatim rather than
   * being dropped, for the same reason as ACTION_LABELS.
   */
  private static readonly TIER_LABELS: Record<string, string> = {
    low: 'Low',
    moderate: 'Moderate',
    high: 'High',
  };

  /**
   * `escalation.tier` as the NCM queue's PRIORITY. This is the one place the two vocabularies meet:
   * the BRD's Moderate is the platform's `'medium'`, and a task whose body reads 'Tier: Moderate'
   * while its chip reads High is telling the NCM two different things about the same finding
   * (Andres Rodriguez, 2026-09-10, on `Weight gain >=3 lbs over 3 days (Moderate tier)`).
   *
   * `'critical'` is deliberately unreachable. It exists on `ClinicalTask` and the §4.6 job assigns
   * it (high acuity AND overdue), but §4.1 has three tiers and no fourth, so mapping `high` onto it
   * would invent a severity the protocol does not define. `escalation.action: 'emergent'` is not it
   * either — the emergent finding is that emergency services were ALREADY activated on the call.
   *
   * A tier the map has never heard of yields `undefined`, and `escalationTask` then writes `'high'`
   * — the pre-BRD value. A guide document can be edited straight in Firestore, so an unknown tier
   * must degrade to the historical priority rather than to `undefined` on the document.
   *
   * ## `low: 'low'` only ever reaches a SYMPTOMATIC finding — flagged for clinical sign-off
   *
   * Worth stating plainly rather than leaving it to fall out of the table (juana98, PR review,
   * 2026-09-10). Only `owner === 'rn'` findings become tasks, and a Low band is `owner: 'ma'` until
   * its `symptom_gate` opens — so the ONLY Low-tier task that can exist is one where the patient
   * reported edema or shortness of breath, and that is the task this row moves from `'high'` to
   * `'low'`. The most concerning Low variant takes the largest downgrade, and it is the only one
   * that ships.
   *
   * It is still the right mapping: §4.1 gives the acuity upgrade to the NCM — "NCM reviews and
   * assesses for acuity upgrade to Moderate" — so promoting it here would perform that assessment
   * silently, with the body still reading `Tier: Low`. But it is a CLINICAL call about a symptomatic
   * patient's queue position, so it wants Marcela's explicit sign-off rather than this component's
   * inference from a lookup table. Recorded here and in the PR body; the code is deliberately not
   * hedged in the meantime, because writing `'medium'` for a finding whose body says `Tier: Low` is
   * the exact disagreement this whole change removes.
   *
   * ## The queue POSITION follows, and it is not this component that writes it
   *
   * The NCM's Open Tasks list does not sort on `priority` — it sorts on `priority_score` descending
   * (`task-history.component.ts`, `sortByField: 'priority_score'`), so a chip that changed while the
   * score did not would move the colour and leave the row where it was, in the very window this
   * change is about (juana98, PR review, 2026-09-10).
   *
   * The score is derived from the priority DOWNSTREAM, by welby-functions' `handleTask` trigger on
   * `clinical_tasks/{taskId}` onCreate. It reads `welby_configuration/default_task_priority` and
   * writes `priority_score` UNCONDITIONALLY — no "only if absent" guard, unlike the `task_name_lower`,
   * `priority`, `category` and `patient_name` writes beside it. Measured on welby-dev 2026-09-10:
   *
   * | priority written here | `priority_score` the trigger writes |
   * |---|---|
   * | `critical` | 10 |
   * | `high` | 8 |
   * | `medium` | 6 |
   * | `low` | 3 |
   *
   * So the Moderate finding this change is about moves from 8 to 6, and the Low one to 3, without
   * this component writing a number. Writing one HERE would be worse than redundant: the trigger
   * would overwrite it a moment later, and it would copy a table that lives in a Firestore document
   * precisely so it can be retuned without a release — a copy that could not be retuned with it.
   *
   * It is also the second reason the §4.6 `task-prioritization` job is the other half of the defect:
   * it rewrites BOTH fields together on its next run, from acuity and overdue days, having never
   * heard of a tier.
   */
  private static readonly TASK_PRIORITY_BY_TIER: Record<string, ClinicalTask['priority']> = {
    low: 'low',
    moderate: 'medium',
    high: 'high',
  };

  /**
   * The STAT tier marker at the head of an `escalate_note`, as `general_ma` and `activation_ma`
   * write it today — the two guides this banner has been verified against. It must match byte for
   * byte, em dash included, or `statProtocolTriggered` silently classifies the field as non-STAT
   * and the 911 banner never appears; there is no error for getting it wrong, which is why it is a
   * named constant rather than an inline string in the predicate.
   *
   * `copd_ma`, `t2dm_ma`, `chf_ma` and `htn_ma` do not use this marker — their emergency fields
   * still escalate via `escalate_when`, they just render without this banner. Extending coverage
   * to them needs those seeds to adopt one shared marker; it is not something this component can
   * fix on its own.
   */
  private static readonly STAT_NOTE_PREFIX = 'EMERGENCY SYMPTOM — ESCALATE IMMEDIATELY.';

  /**
   * Ceiling on the roster read that resolves an escalation's target.
   *
   * It sits inside the note-generation `forkJoin`, so without a bound a hanging request holds the
   * spinner up after the note itself has already been written, and the MA has nothing to act on.
   */
  private static readonly ROSTER_TIMEOUT_MS = 10_000;

  /**
   * Ceiling on a finding's captured value.
   *
   * Inherited from `CareCallFindingDto.finding_value`, which was `@MaxLength(500)` on the alert
   * writer. That writer is no longer called from here — the guide creates tasks, not alerts — so the
   * clip is now for READABILITY: the value goes into a task body an NCM reads in a list, and a
   * seed-authored free-text answer running to thousands of characters buries the finding under it.
   * Kept at the same number so a value captured before and after this change reads identically.
   */
  private static readonly FINDING_VALUE_MAX_LENGTH = 500;

  /** The pre-BRD task name, now the PREFIX of every per-reason name. Never changes. */
  private static readonly ESCALATION_TASK_NAME = 'Care Call Escalation Review';

  /** Ceiling on the reason appended to that name — see `escalationTaskName`. */
  private static readonly TASK_NAME_REASON_MAX_LENGTH = 60;

  /**
   * Dedup key of the pre-BRD AGGREGATE task, the one a clinic outside the canary still gets.
   *
   * Carries a colon so it can never collide with a per-reason key, which is always
   * `sectionId.fieldId`. One key for the whole call reproduces the old boolean dedup exactly.
   */
  private static readonly AGGREGATE_REASON_KEY = 'call:aggregate';

  @Input() diagnosis!: string;
  @Input() diagnoses: Diagnosis[] | null = null;
  resolvedDiagnosis = '';

  /**
   * BRD-CLIN-2026-006 §8.2 — the patient's diuretic titration screening, or null when it has not
   * been read (yet, or at all). See `diureticGate` for how the two are told apart on screen.
   */
  diureticEligibility: ClinicalEligibilityView | null = null;

  /** True once the read has come back, whatever it came back with. Distinguishes "unknown" from "failed". */
  eligibilityResolved = false;
  guide: CareCallGuideDoc | null = null;
  guideForm!: UntypedFormGroup;
  loading = true;
  generatingNote = false;
  /** Minimum selectable date for date fields (today) — blocks past dates in the picker. */
  today = new Date();
  /**
   * Somebody re-seeded or migrated the guide document while THIS form was open (§3.5a).
   *
   * Deliberately inert: nothing branches on it. It exists so the fact can be attached to the
   * generated note or to the alert later, if audit asks "was the guide edited during this call?".
   * Do not wire it into escalation logic — the answers, the alert and the note all stay pinned to
   * the revision the MA actually filled, which is the whole point of the guard that sets it.
   */
  guideRevisedWhileOpen = false;

  /**
   * Whether THIS patient's clinic runs BRD-CLIN-2026-006 **and** this MA is one of the four it was
   * rolled out to (CHF-E3 §3.6).
   *
   * Resolved ONCE, before the guide document is turned into a form, because it decides which
   * fields exist at all — see care-call-guide-scope.util.ts. Fails closed: a patient with no
   * clinic, a missing client document, a missing toggle, a caller outside the canary, a missing
   * canary document and a read error on either all leave it `false`, and the MA gets the pre-SOC
   * guide.
   *
   * It is NOT the gate that authorises writing a task. That one is re-read at write time by
   * `careCallAlertsEnabledForClinic$()` on purpose — two independent reads, so a bug in the
   * rendering filter cannot by itself route an Emory-protocol finding into another clinic's queue,
   * or into one written by an MA outside the canary.
   */
  socScopeEnabled = false;

  private unsubscribe$ = new Subject<void>();
  /**
   * The reason keys (`sectionId.fieldId`) this capture has ALREADY written a task for.
   *
   * A boolean cannot express the outcome any more. One task per reason means a call can end
   * HALF-written — three POSTs accepted, the fourth refused — and two things must then hold:
   * generating the note again writes the missing ones, and it does not duplicate the accepted ones.
   * A key is added only after its own POST succeeds, so a retry writes exactly the remainder.
   *
   * This DOES widen the old boolean dedup (PR review finding 5 kept it deliberately narrow), and
   * the widening is forced by the split: a reason added after the first generation now gets its own
   * task. Under the aggregate task that would have meant rewriting a body the NCM may already have
   * read; a NEW task for a NEW reason has no such collision.
   */
  private writtenEscalationKeys = new Set<string>();
  /**
   * Re-entrancy guard for the run itself, cleared however the run ends (`finalize`). It stops two
   * overlapping runs; it is NOT what stops duplicates — `writtenEscalationKeys` is.
   */
  private escalationInFlight = false;
  /**
   * Set when the provider-roster read failed rather than legitimately resolving nobody. Only drives
   * which sentence the MA reads — the routing outcome is identical (no assignee, no task), because
   * falling back to the licensure rule when the roster is unavailable would reproduce exactly the
   * cross-lane bug §0d removes.
   */
  private rosterUnavailable = false;
  /**
   * Set once the note is generated: the form's lifecycle is over, so no further cache
   * writes happen (including in-flight debounced saves and the destroy-time flush) —
   * otherwise a pending save could resurrect the just-cleared entry.
   */
  private formCacheFinalized = false;
  /**
   * Patient this component instance was initialized for. All cache reads/writes are keyed
   * to it (never the live currentPatientServiceID), so if the component instance were ever
   * reused across a patient switch, this form can never be persisted into another patient's
   * cache slot.
   */
  private cachePatientId: string | null = null;

  constructor(
    private careCallGuideService: CareCallGuideService,
    private generateNoteService: GenerateNoteService,
    private clinicalNoteCacheService: ClinicalNoteCacheService,
    private formCacheService: CareCallGuideFormCacheService,
    private patientService: PatientService,
    private providerService: ProviderService,
    private clientsService: ClientsService,
    private authService: NewAuthService,
    private fb: UntypedFormBuilder,
    private snackService: SnackService,
    private route: ActivatedRoute,
    private clinicalEligibilityService: ClinicalEligibilityService,
  ) {}

  get displayTitle(): string {
    return this.guide?.title.replace(' Guide', '').trim() ?? '';
  }

  get sortedSections(): GuideSection[] {
    return this.guide?.sections.slice().sort((a, b) => a.order - b.order) ?? [];
  }

  /**
   * The caller's role for THIS patient, from their clinical licensure on the care team.
   *
   * Resolved per patient rather than read off a role flag — see resolveCareCallRole for why
   * `roles.isManagerNurse` was the wrong input. A user who is not on the care team resolves to
   * 'ncm', which is the side that produces nothing.
   */
  get careRole(): GuideRole {
    return resolveCareCallRole(this.authService.user, this.patientService.currentPatientService);
  }

  get suppressEscalations(): boolean {
    return this.careRole === 'ncm';
  }


  get hasFormData(): boolean {
    if (!this.guideForm) {
      return false;
    }
    return Object.values(this.guideForm.value as Record<string, Record<string, GuidePrimitive>>).some((section) =>
      Object.values(section).some((v) => v !== '' && v !== false && v !== null),
    );
  }

  /**
   * Whether the §8.2 banner belongs on screen at all: a CHF call, at an onboarded clinic, with the
   * read back.
   *
   * `socScopeEnabled` is repeated here rather than left implicit in "the loader would not have run".
   * That is true today, and it is one reordering away from not being true — and the failure it would
   * hide is a clinic being shown an exclusion notice for a protocol it does not run.
   */
  get showsDiureticGate(): boolean {
    return this.resolvedDiagnosis === 'chf' && this.socScopeEnabled && this.eligibilityResolved;
  }

  /**
   * Is a §8.2 screen actually due for this patient?
   *
   * TRUE means ASK. The screening questions render only in the states where the answer would change
   * something: never screened, a verdict that has aged out, or a record that could not be read.
   * A current verdict — eligible or excluded — hides them, because re-asking on every call turns a
   * ninety-day screen into a daily chore and invites the MA to click through it.
   *
   * FAILS TOWARD ASKING, which is the opposite direction from `mayTitrate` and deliberately so. An
   * unreadable record shows the questions: a redundant screen costs half a minute, while a skipped
   * one leaves a patient recorded as screened who never was. `mayTitrate` fails the other way for
   * the same reason — neither absence of a record nor absence of an answer may read as permission.
   *
   * Waits for the read to resolve (`eligibilityResolved`) so the block cannot flash in and vanish
   * mid-sentence while an MA is reading it aloud.
   */
  get diureticScreenDue(): boolean {
    if (!this.showsDiureticGate) {
      return false;
    }
    const view = this.diureticEligibility;
    return !view || view.expired === true || view.status === EligibilityStatus.NOT_SCREENED;
  }

  /** True when Welby-driven titration must NOT be offered. Everything except an explicit, unexpired `eligible`. */
  get diureticGateBlocked(): boolean {
    return !mayTitrate(this.diureticEligibility);
  }

  /**
   * What the banner says.
   *
   * The three blocked states are worded DIFFERENTLY on purpose, because they call for different
   * actions: an exclusion is final and names its reason, "not screened" is a prompt to screen, and
   * an unreadable record is a system problem the clinician should not mistake for either. Collapsing
   * them into one "not eligible" would tell an MA nothing about what to do next.
   */
  get diureticGateLabel(): string {
    const view = this.diureticEligibility;
    if (!view) {
      return 'Diuretic titration screening could not be read — treat as not screened';
    }
    if (view.status === EligibilityStatus.EXCLUDED) {
      const reasons = (view.reasons ?? []).map((reason) => EXCLUSION_REASON_LABELS[reason] ?? reason);
      return reasons.length
        ? `Excluded from Welby-driven diuretic titration — ${reasons.join(', ')}`
        : 'Excluded from Welby-driven diuretic titration';
    }
    if (view.status === EligibilityStatus.ELIGIBLE && view.expired === true) {
      return 'Diuretic titration screening has expired — re-screen before titrating';
    }
    if (view.status === EligibilityStatus.ELIGIBLE) {
      return 'Screened: eligible for Welby-driven diuretic titration';
    }
    return 'Not screened for diuretic titration — screening required before any dose change';
  }

  /**
   * A delta rendered as the guide shows it: explicit sign, one decimal, optional unit
   * ("+4.0 lbs", "-5.0 lbs"). Shared by the on-screen computed row and by the value written onto a
   * care-call alert, so the two can never disagree about the same reading.
   */
  private static signedDelta(value: number, unit?: string): string {
    const sign = value < 0 ? '-' : '+';
    return `${sign}${Math.abs(value).toFixed(1)}${unit ? ' ' + unit : ''}`;
  }

  /**
   * A provider's `rank` as a sortable number; anything unusable sorts LAST, never as 0.
   *
   * `Number(undefined)` and `Number('x')` are both NaN, and a NaN comparator result is treated as 0
   * by the sort — which silently degrades ordering back to array order instead of failing.
   */
  private static rankOf(rank: number | undefined): number {
    const numeric = Number(rank);
    return Number.isFinite(numeric) ? numeric : Number.MAX_SAFE_INTEGER;
  }

  ngOnInit(): void {
    // The re-emission guard below pins the form to the first valid revision for the lifetime of one
    // LOAD, not of the object. Clearing here keeps "load" the right unit, so a second `ngOnInit` can
    // accept a document again instead of being frozen on the first one forever.
    //
    // It does NOT make a second `ngOnInit` safe on its own: `unsubscribe$` is never re-created, so a
    // re-init after `ngOnDestroy` would subscribe into a completed teardown, and one WITHOUT a destroy
    // would leave two live subscriptions racing on `this.guide`. Angular calls this once per instance
    // and nothing here calls it explicitly, so neither happens — but do not add a caller expecting it
    // to work.
    this.guide = null;
    this.guideRevisedWhileOpen = false;
    this.socScopeEnabled = false;
    this.resolvedDiagnosis = this.diagnosis || this.route.snapshot.queryParams['diagnosis'] || 'chf';
    // The clinic gate is resolved BEFORE the document, not alongside it: it decides which fields
    // exist, so it has to be known by the time `buildForm` runs. `isCareCallAlertsEnabled$`
    // completes (`take(1)`) and swallows its own errors into `false`, so this switchMap runs
    // exactly once and can never leave the guide unloaded because a `clients` read failed.
    //
    // TWO DEPENDENCIES THIS ORDERING RESTS ON, BOTH LIVING OUTSIDE THIS FILE. Named here because
    // each breaks SILENTLY — the guide still loads, it just loads with the wrong side of the gate:
    //
    // 1. `ClientsService.isCareCallAlertsEnabled$` must keep its `take(1)`. Without it `doc$` keeps
    //    emitting, so a later `clients` write would reassign `socScopeEnabled` while `this.guide`
    //    stays pinned to the scoping it was built with (the pin below blocks the rebuild) — the
    //    flag and the rendered form would then disagree about which clinic this call belongs to.
    // 2. `careCallAlertsEnabledForClinic$()` is handed `patientService.currentPatientService` read
    //    SYNCHRONOUSLY here, and falls closed to `false` without one. That holds only because
    //    `care-call-guide-home.component.ts` returns early with no patient id and renders this
    //    panel after `getPatientDiagnoses` resolves. Mount the guide anywhere that does not
    //    guarantee the patient first and an onboarded clinic silently gets the legacy form.
    // 3. `CareCallGuideService.isCareCallCanaryUser$` must keep its `take(1)` for the same reason
    //    as (1): it is the second half of the same `forkJoin`, so a stream that never completes
    //    leaves the guide unloaded entirely rather than loading the wrong side of the gate.
    this.careCallAlertsEnabledForClinic$(this.patientService.currentPatientService)
      .pipe(
        switchMap((socEnabled) => {
          this.socScopeEnabled = socEnabled;
          // Started HERE, once the clinic gate is known, and still NOT composed into this stream —
          // it is kicked off beside it, so a slow eligibility read cannot hold the form on a
          // spinner. Before the gate it would have fired on every CHF call at all 40 clinics.
          this.loadDiureticEligibility();
          return this.careCallGuideService.getGuide(this.resolvedDiagnosis);
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe({
        next: (doc) => {
          if (!doc || !doc.sections?.length) {
            this.loading = false;
            this.snackService.genericSnackBar('No care call guide available for your role yet', ['error-snackbar'], 6000);
            return;
          }
          // `getGuide` is a live listener on a GLOBAL config document (one `chf_ma` for all 40
          // clinics), so a re-emission means somebody re-seeded or migrated the guide while this
          // form was open — it never means this MA's call changed. Rebuilding would hand them an
          // empty form, and `restoreCachedForm` would then find a version that no longer matches.
          // Pin the form to the first valid revision for the lifetime of this instance: the answers
          // survive, and `guide_version` on the alert and the note stays the revision the MA
          // actually filled, which is what §5.1 auditability requires (AC-6).
          //
          // NOT `take(1)` on the stream: the first emission of a missing or half-written document is
          // `undefined`, which the guard above rejects — completing the stream there would mean the
          // component could never recover when the document appeared. `this.guide` is assigned only
          // on the accepted path, so the self-heal is preserved.
          //
          // DELIBERATELY SILENT. There is no snackbar here on purpose: `doc$` is a live listener, so
          // it re-emits on ordinary snapshot churn (cache-then-server, a metadata change, any write
          // to the global document by a seed or a migration) — none of which is an event the MA did
          // anything to cause or can do anything about. Their form is already protected by the pin
          // below, so a toast on every re-emission was pure noise mid-call. Do not add one back: if
          // the fact ever needs surfacing, it belongs on the generated note via
          // `guideRevisedWhileOpen`, not in the MA's face while they are on the phone.
          if (this.guide) {
            this.guideRevisedWhileOpen = true;
            return;
          }
          // Everything downstream reads `this.guide`, so scoping HERE — once, before the form is
          // built — is what makes the filter total: an out-of-scope clinic has no control, no
          // escalation walk and no note line for a §4.1 field, because the field is not in the
          // document this call is running against. `version` is deliberately left alone: it is the
          // revision of the document, and both scopings come from the same one.
          const scoped = scopeGuideToClinic(doc, this.socScopeEnabled);
          if (!scoped.sections.length) {
            // Only reachable if a future seed marks EVERY section `scope: 'soc'`. Treated exactly
            // like a missing guide rather than rendering an empty form with a working note button.
            this.loading = false;
            this.snackService.genericSnackBar('No care call guide available for your role yet', ['error-snackbar'], 6000);
            return;
          }
          const orphans = findOrphanedScopeReferences(scoped);
          if (orphans.length) {
            // Silent by nature, and in more than one direction: a `visible_when` orphan means the
            // question never renders, while a `symptom_gate` or `value_from` orphan means a §4.1
            // finding is HELD or reaches the queue with no measurement — under-escalation that
            // reads exactly like a normal call. Asserted at seed time in welby-firestore (S4);
            // logged here because the LIVE document can be written by a migration that never ran
            // those invariants. Not repaired at runtime — dropping a rule would show a question
            // its author meant to keep conditional.
            console.warn('[care-call-guide] reference points at a field removed by clinic scoping:', orphans);
          }
          this.guide = scoped;
          // Pin the cache to the patient this instance loaded for (used for every cache op below).
          this.cachePatientId = this.patientService.currentPatientServiceID ?? null;
          this.buildForm(scoped);
          this.setupAutoChecks(scoped);
          this.restoreCachedForm(scoped);
          this.setupFormPersistence();
          this.loading = false;
        },
        error: () => {
          this.snackService.genericSnackBar('Could not load care call guide', ['error-snackbar'], 6000);
          this.loading = false;
        },
      });
  }

  ngOnDestroy(): void {
    // Flush a final save so edits made within the debounce window right before
    // navigating away (e.g. to RPM Data) are not lost.
    this.persistFormToCache();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /** Current value of a control, addressed by section + field id. */
  controlValue(sectionId: string, fieldId: string): GuidePrimitive {
    return this.guideForm?.get(`${sectionId}.${fieldId}`)?.value ?? null;
  }

  /**
   * Keeps a `numeric_slash` text field (e.g. Blood Pressure "120/80") to digits and "/" only —
   * strips any other character as the user types or pastes. Escalation still reads the systolic
   * value via parseFloat (the leading number before "/").
   */
  sanitizeNumericSlash(sectionId: string, fieldId: string): void {
    const control = this.guideForm?.get(`${sectionId}.${fieldId}`);
    if (!control) {
      return;
    }
    const cleaned = String(control.value ?? '').replace(/[^0-9/]/g, '');
    if (cleaned !== control.value) {
      control.setValue(cleaned, { emitEvent: false });
    }
  }

  /**
   * Conditional rendering — a field is shown unless its `visible_when` rule fails.
   *
   * This is the single seam the rest of the component reads: `fieldEscalates`, `buildPromptResponses`
   * and `collectCapturedResponses` all call it, so a question hidden here is also absent from the
   * escalation walk, the generator prompt and the note.
   */
  isFieldVisible(section: GuideSection, field: GuideField): boolean {
    return this.resolveFieldVisible(section, field, new Set<string>());
  }

  /**
   * `isFieldVisible`, carrying the fields already being resolved so a chain cannot loop.
   *
   * A rule is satisfied only when the field it watches is ITSELF visible, not merely holding a
   * matching value. Hiding a field does not clear its control (see `buildPromptResponses`), so a rule
   * that read the raw value alone kept a chained question on screen — and escalating off it — after
   * the answer above it had been retracted.
   *
   * A cycle is a seeding error: the re-entered field resolves as visible so the chain terminates,
   * which leaves every rule still resolving against its own answer rather than forcing the cycle open.
   *
   * `latch` is subordinate to the cascade. It keeps a field on screen when its trigger's VALUE moves
   * away, never when the trigger itself leaves the form.
   */
  private resolveFieldVisible(section: GuideSection, field: GuideField, resolving: Set<string>): boolean {
    // §8.2's screening block, asked only when a screen is actually DUE. Ahead of `visible_when`
    // because the two compose: a screening field may also carry a sibling rule and both must pass.
    if (field.screen_gate === DIURETIC_SCREEN_GATE && !this.diureticScreenDue) {
      return false;
    }
    const rule = field.visible_when;
    if (!rule) {
      return true;
    }
    // `any_of` is OR'd across siblings, `field` watches one. A rule carrying neither names nothing to
    // watch: shown, matching the `!rule` case above, because the alternative is a question that can
    // never render — the same silent under-asking `findOrphanedScopeReferences` reports.
    const watched = rule.any_of?.length ? rule.any_of : rule.field ? [rule.field] : [];
    if (!watched.length) {
      return true;
    }

    const key = `${section.id}.${field.id}`;
    if (resolving.has(key)) {
      return true;
    }
    resolving.add(key);
    try {
      // An id naming no field in this section falls through to the value check, which reads `null` and
      // fails the rule — unchanged from before the cascade, and already reported by the seed invariants.
      const watchedVisible = (watchedId: string): boolean => {
        const parent = section.fields.find((candidate) => candidate.id === watchedId);
        return !parent || this.resolveFieldVisible(section, parent, resolving);
      };

      // Resolved once and reused: this runs from an `*ngIf` on every field on every change-detection
      // cycle, and the latch branch below would otherwise walk the whole ancestor chain a second time
      // (alejg2, PR #1267 review).
      const visibleWatched = watched.filter(watchedVisible);
      if (visibleWatched.some((watchedId) => this.visibilityRuleMatches(rule, this.controlValue(section.id, watchedId)))) {
        return true;
      }
      if (rule.latch !== true || !visibleWatched.length) {
        return false;
      }
      // `dirty` as well as a value: a latch on the VALUE alone un-renders the field the moment the MA
      // clears it to correct it, taking an edit in progress with it (juana98, PR #1264 review).
      // A restored form is PRISTINE (`patchValue`) and `applyAutoChecks` uses `setValue`, so neither
      // dirties anything and a band ticking itself never latches a block open.
      const control = this.guideForm?.get(`${section.id}.${field.id}`);
      return this.isAnswered(control?.value) || control?.dirty === true;
    } finally {
      resolving.delete(key);
    }
  }

  /** True when a field's value trips any of its escalation rules. */
  fieldEscalates(section: GuideSection, field: GuideField): boolean {
    // NCMs diligence the form themselves — no escalation flags or NCM task for them.
    if (this.suppressEscalations) {
      return false;
    }
    if (!this.isFieldVisible(section, field)) {
      return false;
    }
    const value = this.controlValue(section.id, field.id);
    if (field.escalate_when !== undefined && value === field.escalate_when.equals) {
      return true;
    }
    const numeric = this.numericValue(section.id, field.id);
    if (numeric === null) {
      return false;
    }
    return this.numericBoundHit(numeric, {
      above: field.escalate_above,
      below: field.escalate_below,
      at_or_above: field.escalate_at_or_above,
      at_or_below: field.escalate_at_or_below,
    });
  }

  /**
   * True when a field tripped IN THIS SECTION carries the STAT tier.
   *
   * The tier is read off the `escalate_note` prefix rather than from a field of its own — the
   * `general_ma` and `activation_ma` seeds write it there today, and reading the same marker here
   * keeps the banner and the captured-value note in sync FOR THOSE GUIDES. Other guides' seeds use
   * different wording for their emergency fields (see `STAT_NOTE_PREFIX`) and will not raise this
   * banner even though the field still escalates.
   *
   * PURELY VISUAL. It raises nothing, routes nothing and creates no task — the fields it watches
   * already escalate on their own through `escalate_when`. It exists so the 911 instruction is
   * on screen as a block the moment an emergency symptom is ticked, instead of only as the tail of
   * one field's alert line.
   *
   * Skips `info` and `computed` fields even though `fieldEscalates` itself applies no type filter
   * (the per-field alert's own guard lives in the template, html:167) — no seed today gives a
   * `computed` field an `escalate_note`, but if one did, `computed` renders as a display value with
   * no field alert beside it, so this predicate excludes both rather than raise a banner with
   * nothing on screen for it to point at.
   */
  statProtocolTriggered(section: GuideSection): boolean {
    return (section.fields ?? []).some(
      (field) =>
        field.type !== 'info' &&
        field.type !== 'computed' &&
        this.fieldEscalates(section, field) &&
        !!field.escalate_note?.startsWith(CareCallGuideBaseComponent.STAT_NOTE_PREFIX),
    );
  }

  /** Reminder text shown on a tripped field. */
  escalationMessage(field: GuideField): string {
    if (field.escalate_note) {
      return field.escalate_note;
    }
    if (field.escalate_above !== undefined) {
      return `Escalation threshold exceeded (> ${field.escalate_above}${field.unit ? ' ' + field.unit : ''})`;
    }
    if (field.escalate_below !== undefined) {
      return `Escalation threshold exceeded (< ${field.escalate_below}${field.unit ? ' ' + field.unit : ''})`;
    }
    if (field.escalate_at_or_above !== undefined) {
      return `Escalation threshold exceeded (>= ${field.escalate_at_or_above}${field.unit ? ' ' + field.unit : ''})`;
    }
    if (field.escalate_at_or_below !== undefined) {
      return `Escalation threshold exceeded (<= ${field.escalate_at_or_below}${field.unit ? ' ' + field.unit : ''})`;
    }
    return 'Escalation required';
  }

  /**
   * Live status banner computed from a field's value (e.g. the graded BP status bar
   * in the source form's evalBP). Bands are evaluated in declared order and the first
   * whose rules match wins; each band's `rules` are OR'd so it can grade off either
   * component of a split reading (SBP or DBP). A band with no `rules` is a catch-all
   * (matches once the field has a value). Returns null when the field is empty/hidden
   * or has no status_bands. MA-only: the bands carry escalate/notify-NCM guidance,
   * so they are suppressed for NCMs (same rule as escalation flags and auto-checks).
   */
  fieldStatus(section: GuideSection, field: GuideField): GuideStatusBand | null {
    if (this.suppressEscalations || !field.status_bands?.length || !this.isFieldVisible(section, field)) {
      return null;
    }
    // No banner until the driver field has a numeric value (systolic side for a slash field).
    if (this.componentValue(section.id, field.id) === null) {
      return null;
    }
    return (
      field.status_bands.find((band) => {
        if (!band.rules?.length) {
          return true; // catch-all
        }
        return band.rules.some((rule) => {
          const value = this.componentValue(section.id, rule.field ?? field.id, rule.component);
          return value !== null && this.numericBoundHit(value, rule);
        });
      }) ?? null
    );
  }

  /**
   * Raw derived value for a `computed` field: `field - minus_field` (both sibling numeric
   * fields in the same section). Returns null unless BOTH operands hold a number, so the
   * display stays blank until both are filled and clears again if either is emptied. Runs
   * for every role — it is documentation of the typed values, not an escalation.
   */
  computedValue(section: GuideSection, field: GuideField): number | null {
    if (field.type !== 'computed' || !field.compute || !this.isFieldVisible(section, field)) {
      return null;
    }
    const minuend = this.numericValue(section.id, field.compute.field);
    const subtrahend = this.numericValue(section.id, field.compute.minus_field);
    if (minuend === null || subtrahend === null) {
      return null;
    }
    return minuend - subtrahend;
  }

  /**
   * Formatted `computed` value for display — signed, one decimal, with the field's unit
   * (e.g. "+4.0 lbs", "-5.0 lbs"). Empty string when the value is unavailable, which the
   * template uses to hide the row entirely.
   */
  computedDisplay(section: GuideSection, field: GuideField): string {
    const value = this.computedValue(section, field);
    return value === null ? '' : CareCallGuideBaseComponent.signedDelta(value, field.unit);
  }


  /**
   * Whether a `computed` value should render in red. Two thresholds, both on the absolute delta:
   * `red_at_or_above` is INCLUSIVE and wins when present, `red_when_abs_above` is exclusive.
   *
   * Both forms exist so a guide can align the red row with whichever bound its band uses — an
   * inclusive band beside an exclusive red row (or the reverse) shows the MA a highlighted delta
   * next to an unticked box. The CHF guide uses one of each: 3-day Moderate is `at_or_above: 3`
   * and red-flags inclusively, 24-hour Moderate is `above: 2` and red-flags exclusively, because a
   * gain of exactly 2 lbs ticks the Low box there. Fields declaring neither always render in the
   * standard color.
   */
  computedIsRed(section: GuideSection, field: GuideField): boolean {
    // `typeof === 'number'`, not `!== undefined`: guide docs are edited in Firestore without a
    // frontend release, and a field blanked to `null` in the console rather than deleted would make
    // `magnitude >= null` coerce to `magnitude >= 0` — every delta permanently red, which is alarm
    // fatigue on the one visual cue that marks an escalating weight.
    const inclusive = typeof field.red_at_or_above === 'number' ? field.red_at_or_above : undefined;
    const exclusive = typeof field.red_when_abs_above === 'number' ? field.red_when_abs_above : undefined;
    if (inclusive === undefined && exclusive === undefined) {
      return false;
    }
    const value = this.computedValue(section, field);
    if (value === null) {
      return false;
    }
    const magnitude = Math.abs(value);
    return inclusive !== undefined ? magnitude >= inclusive : magnitude > exclusive;
  }

  /**
   * Generates the AI clinical note and writes it straight into the patient's
   * Clinical Notes editor (the CKEditor in case-note-form) via the shared
   * ClinicalNoteCacheService. The note is NOT persisted here — the user reviews
   * and completes it from the Clinical Notes panel.
   *
   * Four outcomes, deliberately distinguished — a note that supports billing must never be
   * announced as complete when it is not:
   * - complete note → written, success snackbar, form cache dropped (lifecycle closed);
   * - `truncated` → written (nothing generated is discarded) with a warning, cache KEPT so a
   *   regeneration is one click away;
   * - `empty` → the AI produced nothing usable, so this takes the fallback path (captured
   *   responses + error snackbar) instead of writing a narrative-less note under a success toast;
   * - active patient changed mid-request → nothing written, and the user is told.
   */
  generateNote(): void {
    this.flushPendingInput();
    this.generatingNote = true;
    // Snapshot at click time: the captured responses, the patient-context header, the section
    // outline and the narrative-less fallback must all describe the same patient and the same form
    // state the user was looking at. Building any of them at response time would misattribute the
    // note if the active patient changed while the request was in flight, and `this.guide` is a
    // live Firestore subscription, so the outline could otherwise come from a newer guide revision
    // than the responses it titles.
    const captured = this.collectCapturedResponses();
    const contextHtml = this.buildPatientContextHtml();
    const responses: GuideFormResponse = this.buildPromptResponses();
    const outline = this.noteSectionOutline(captured, responses);
    const capturedOnlyHtml = this.buildCapturedOnlyHtml(captured, outline);

    // Note generation and NCM escalation run in parallel via forkJoin. Each stream
    // owns its own catchError so a failure in one never aborts the other — the
    // escalation must not be dropped just because the AI note failed (and vice versa).
    const note$ = this.generateNoteService
      .generateCareCallNote(this.resolvedDiagnosis, this.careRole, responses, this.buildFieldLabels(), this.collectEscalations())
      .pipe(
        tap((generated) => {
          if (!this.canWriteToActivePatientNote()) {
            // Silently swallowing this left the spinner stopping with no note and no explanation.
            this.snackService.genericSnackBar(
              'Patient changed during generation — the note was not added',
              ['warn-snackbar'],
              6000,
            );
            return;
          }
          // No usable note text: writing it would put the header and the bare captured responses in
          // the chart with no narrative at all, under a success snackbar. Same as a failed call.
          if (generated.empty) {
            this.reportFailedNote('The generator returned an empty note', contextHtml, capturedOnlyHtml);
            return;
          }
          this.clinicalNoteCacheService.setNoteContent(
            contextHtml + this.buildNoteSectionsHtml(generated.note, captured, responses, outline),
          );
          // Open the Clinical Notes panel if it isn't already, so the pasted note is visible.
          if (!this.patientService.creatingNote) {
            this.patientService.toggleNoteView();
          }
          if (generated.truncated) {
            // The note IS written — nothing generated is thrown away — but it is missing content
            // the model was supposed to include, so it must not be reported as complete. The form
            // cache is kept (no clearFormCache) so the MA can regenerate without re-entering it.
            this.snackService.genericSnackBar(
              'Note may be incomplete — review it against the captured responses before completing',
              ['warn-snackbar'],
              8000,
            );
          } else {
            this.snackService.genericSnackBar('Note added to Clinical Notes', ['success-snackbar'], 3000);
            // A complete note closes the form's lifecycle — drop the cached in-progress state.
            this.clearFormCache();
          }
          this.scrollToNotePanel();
        }),
        catchError(() => {
          this.reportFailedNote('Error generating note', contextHtml, capturedOnlyHtml);
          return of(null);
        }),
      );

    forkJoin([note$, this.raiseCareCallEscalations$(), this.attestDiureticScreen$()])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.generatingNote = false;
      });
  }

  /**
   * Commits any pending edit before reading the form. Reactive `matInput` controls already
   * update on every keystroke (`updateOn: 'change'` = the `input` event), but a date *typed*
   * into a `mat-datepicker` and IME compositions only commit on blur: blurring the active
   * element closes that gap for the click that goes straight from a field to "Generate Note".
   *
   * It has to run on the button's `(mousedown)`, which is why the template hooks it there and it
   * is public. By `(click)` time the browser has already moved focus to the button, so
   * `document.activeElement` is no longer the input and the call inside `generateNote()` can never
   * flush anything — it is kept only for callers that invoke `generateNote()` programmatically
   * (tests, keyboard activation), where focus never sat in a field to begin with.
   */
  flushPendingInput(): void {
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
      active.blur();
    }
  }

  /**
   * A response carries information unless it is empty or an unticked checkbox.
   * `0` DOES count (a Pain Score of 0 or a duration of 0 are real data).
   */
  private isAnswered(value: GuidePrimitive | Date): boolean {
    if (value === null || value === undefined || value === false) {
      return false;
    }
    return typeof value === 'string' ? value.trim().length > 0 : true;
  }

  /** One watched sibling's value against the rule's match clause. See `GuideVisibilityRule`. */
  private visibilityRuleMatches(rule: GuideVisibilityRule, value: GuidePrimitive): boolean {
    // `answered` shows the field once the sibling has any value picked (e.g. either Y or N).
    // Restriction: only `answered: true` is supported (there is no `answered: false` inverse),
    // and a `0` / `false` selection counts as answered — fine for the Y/N radio siblings this
    // gates today, but a numeric/boolean sibling defaulting to 0/false would read as answered.
    if (rule.answered) {
      return value !== '' && value !== null && value !== undefined;
    }
    if (rule.in) {
      return rule.in.includes(value);
    }
    return value === rule.equals;
  }

  /**
   * Responses sent to the generator: a PRUNED copy of `guideForm.value` (dropping `''`, `null`
   * and unticked checkboxes). Pruning the FIELDS matters: a guide like general_ma has 115 controls and a typical call answers 5–15 —
   * sending the ~100 empty ones buries the signal and pushes the model toward reporting nothing.
   *
   * A section that ends up with no answered field is still SENT, carrying a single
   * `not_addressed` marker instead of its empty fields. That is what lets the note distinguish
   * "the guide asked about this and it was not addressed" (print the section) from "this guide has
   * no such section at all" (omit it) — one marker per section, so the token win of pruning the
   * fields is kept.
   *
   * A field hidden by its `visible_when` rule is pruned too. Nothing clears a control when its
   * parent stops matching (`isFieldVisible` only gates the `*ngIf`), so an MA who answers
   * "Chest pain? Yes" + "sharp, radiating to left arm" and then switches the radio to "No" leaves
   * the follow-up text behind: sending it had the model narrate a red-flag symptom the visible
   * answer denies. Mirrors `collectCapturedResponses` and `fieldEscalates`, which apply the same
   * rule — what is off the screen is not part of the call.
   *
   * A section with nothing answerable in it is the exception and is dropped entirely. `buildForm`
   * creates no control for `info` / `computed` fields, so a pure script or talking-points section
   * ("Opening — read this first") yields an empty FormGroup; marking it `not_addressed` would
   * document a clinical topic as asked-and-left-uncovered in a note that supports billing, when
   * the guide never asked anything. No current guide has such a section — verified against the
   * general_ma, copd_ma, t2dm_ma and chf seeds — so this guards the next one. Never mutates the
   * form.
   */
  private buildPromptResponses(): GuideFormResponse {
    const source = this.guideForm.value as Record<string, Record<string, GuidePrimitive | Date>>;
    const pruned: GuideFormResponse = {};
    // On-screen order, not the raw Firestore array order: the prompt tells the model the sections
    // arrive in the order they appear on the form and to keep that order in the note.
    this.sortedSections.forEach((section) => {
      const fields = source[section.id] ?? {};
      // No controls at all = nothing was ever answerable here (display-only section): omit it
      // rather than report it as a topic left blank.
      if (Object.keys(fields).length === 0) {
        return;
      }
      const answered: Record<string, GuidePrimitive> = {};
      Object.keys(fields).forEach((fieldId) => {
        const field = section.fields.find((candidate) => candidate.id === fieldId);
        if (field && !this.isFieldVisible(section, field)) {
          return;
        }
        const value = this.promptValue(fields[fieldId]);
        if (this.isAnswered(value)) {
          answered[fieldId] = value;
        }
      });
      pruned[section.id] =
        Object.keys(answered).length > 0 ? answered : { [CareCallGuideBaseComponent.NOT_ADDRESSED_KEY]: true };
    });
    return pruned;
  }

  /**
   * A control value as the generator should read it. Date controls hold real `Date` objects, which
   * JSON-serialize to a UTC ISO timestamp: the model then reformatted them freely (and a
   * negative-offset timezone could shift the day), so the next check-in date came back in the wrong
   * format. Sending M/D/YYYY leaves the model nothing to reformat. Everything else passes through.
   */
  private promptValue(value: GuidePrimitive | Date): GuidePrimitive {
    return value instanceof Date ? this.formatDate(value) : value;
  }

  /**
   * Readable label for a field. Several guides declare the "if yes → describe" follow-ups
   * with `label: ''` (12 of them in general_ma): without this their free text reaches the
   * prompt anonymous, which is the first thing the model discards. Fallbacks, in order: the
   * label of the `visible_when` parent field + " — details", the `placeholder`, and finally
   * "<section> — <humanized field id>".
   *
   * The last resort humanizes the id rather than passing it through: these labels are read by the
   * model (whose prompt forbids surfacing raw field ids) and rendered verbatim in the note the
   * clinician reads, so "Call Summary — Other Notes" belongs there and "other_notes" does not.
   */
  private fieldLabel(section: GuideSection, field: GuideField): string {
    const own = (field.label ?? '').trim();
    if (own) {
      return own;
    }
    // The single-`field` form only. An `any_of` rule has no ONE parent to borrow a label from, and
    // picking the first entry would name whichever sibling the seed happened to list first — a
    // label that is wrong on every other axis in the set. Such a field falls through to the
    // placeholder / humanized-id fallbacks below instead.
    const parentId = field.visible_when?.field;
    const parentLabel = (section.fields.find((f) => f.id === parentId)?.label ?? '').trim();
    if (parentLabel) {
      return `${parentLabel} — details`;
    }
    const placeholder = (field.placeholder ?? '').trim();
    return placeholder || `${section.label} — ${this.humanizeFieldId(field.id)}`;
  }

  /** "other_notes_2" / "otherNotes2" -> "Other Notes 2". Stays unique per field, exposes no id. */
  private humanizeFieldId(fieldId: string): string {
    return fieldId
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim()
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  /**
   * Every response actually captured, in section and field order, already formatted as text.
   * Covers free text (verbatim), numbers with their unit, radios/selects, dates, ticked
   * checkboxes, the `computed` values the user sees on screen and the NCM escalation reason.
   * This is what each note section is filled with: the "nothing typed is lost" invariant is met
   * here, in code, not in the prompt.
   *
   * "Typed" means typed into a field the MA could see. A field hidden by its `visible_when` rule is
   * skipped: its stale value would render as a `<p>Label: value</p>` row directly under the section
   * heading, contradicting the visible answer right above it ("Chest pain?: No" followed by
   * "Chest pain? — details: sharp, radiating to left arm"). `buildPromptResponses` prunes the same
   * way, so the narrative and the captured rows describe the same call.
   */
  private collectCapturedResponses(): GuideCapturedResponse[] {
    const captured: GuideCapturedResponse[] = [];
    this.sortedSections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.type === 'info' || !this.isFieldVisible(section, field)) {
          return;
        }
        const value =
          field.type === 'computed' ? this.computedDisplay(section, field) : this.formatCapturedValue(section, field);
        if (!value) {
          return;
        }
        captured.push({
          sectionId: section.id,
          sectionLabel: section.label,
          fieldId: field.id,
          label: this.fieldLabel(section, field),
          value,
        });
      });
    });
    return captured;
  }

  /**
   * A control's value resolved to text ('' when it carries no information). Free text is
   * returned EXACTLY as the user typed it — no trimming of inner content, no clipping.
   */
  private formatCapturedValue(section: GuideSection, field: GuideField): string {
    const raw = this.controlValue(section.id, field.id) as GuidePrimitive | Date;
    if (!this.isAnswered(raw)) {
      return '';
    }
    if (raw === true) {
      return 'Yes';
    }
    if (raw instanceof Date) {
      // A date typed by hand into a mat-datepicker can land as an Invalid Date. Saying so beats
      // dropping the row: silence would break the "nothing captured is lost" invariant and the
      // user would get no hint that the date never made it into the note.
      return this.formatDate(raw) || '(invalid date entered)';
    }
    // The unit only qualifies a measurement, so it is appended to numbers and to numeric_slash
    // readings ("138/86 mmHg") — never to a radio/select/free-text answer ("No mmHg").
    const carriesUnit = field.unit && (typeof raw === 'number' || field.numeric_slash);
    return carriesUnit ? `${String(raw)} ${field.unit}` : String(raw);
  }

  /**
   * One captured response as a `<p>Label: value</p>` row. Built as DIRECT HTML
   * (escape + `\n` -> `<br>`) and deliberately NOT routed through `formatNoteAsHtml`, which trims
   * and strips markdown prefixes ("- ", "# ") and would therefore mutilate what the user wrote.
   */
  private capturedEntryHtml(entry: GuideCapturedResponse): string {
    const value = this.escapeHtmlText(entry.value).replace(/\r?\n/g, '<br>');
    return `<p>${this.escapeHtmlText(entry.label)}: ${value}</p>`;
  }

  /**
   * True while this instance may still write into the active patient's Clinical Note. The guide
   * form is pinned to the patient it loaded for (`cachePatientId`), so if the active patient
   * changed while the request was in flight the note must not be written — it would land verbatim
   * PHI from one patient into another patient's chart. Mirrors the guard in `persistFormToCache`.
   */
  private canWriteToActivePatientNote(): boolean {
    // Deliberate fail-open, not a side effect of the `?? null` in ngOnInit: with no id pinned there
    // is nothing to compare against, and the alternative (fail closed) would silently refuse to
    // write any note at all whenever the guide loaded before the patient id resolved. There is no
    // cross-patient risk in that state either — the id being absent means no other patient's
    // chart has been identified as the target.
    if (!this.cachePatientId) {
      return true;
    }
    return this.patientService.currentPatientServiceID === this.cachePatientId;
  }

  /**
   * Common outcome for "no usable note": either the call failed, or it succeeded with an empty
   * note. Both leave the MA with nothing to review, so both write the captured responses as a
   * `fallback` (which never overwrites a note the user already read) and say why. The form cache
   * is deliberately kept — the responses are still needed to retry.
   */
  private reportFailedNote(reason: string, contextHtml: string, capturedOnlyHtml: string): void {
    const wrote = this.writeCapturedFallback(contextHtml, capturedOnlyHtml);
    this.snackService.genericSnackBar(
      wrote ? `${reason} — captured responses added to Clinical Notes` : reason,
      ['error-snackbar'],
      6000,
    );
  }

  /**
   * Fallback that writes the captured responses when the AI call fails, so the MA is never left
   * with nothing. Returns whether anything was written, so the snackbar does not claim otherwise.
   * Marked as a `fallback` write: the merge policy then appends it to an intact generated note
   * rather than replacing it, so a failed regeneration cannot delete prose the user already read.
   */
  private writeCapturedFallback(contextHtml: string, capturedOnlyHtml: string): boolean {
    if (!capturedOnlyHtml || !this.canWriteToActivePatientNote()) {
      return false;
    }
    this.clinicalNoteCacheService.setNoteContent(contextHtml + capturedOnlyHtml, 'fallback');
    if (!this.patientService.creatingNote) {
      this.patientService.toggleNoteView();
    }
    this.scrollToNotePanel();
    return true;
  }

  /**
   * BRD-CLIN-2026-006 §4.1 — raises this call's escalations when an MA completes the form.
   *
   * **THE GUIDE WRITES NO ALERTS. It writes TASKS.** `vital_flags` is the record of a patient's
   * VITAL READINGS, the ones the devices report through RPM; a finding an MA documents on a call is
   * not a reading, and putting it in that collection is what produced rows in the NCM's alert queue
   * whose "Latest Value" column was a question rather than a measurement (Valentina Ariza,
   * 2026-09-07: *"esta lista de alertas es solo para las lecturas de vitales del paciente, que se
   * registran en los dispositivos — las de tipo Abnormal labs, Diuretic Ceiling dose y GDMT
   * Tolerance no se crearían acá"*). What the form produces is work for the NCM, and work is a task.
   *
   * **The §4.1 Low weight band belongs to the device path, because a weight gain is a READING.** It
   * is measured by a scale, not documented on a call, so the engine owns it and this guide was
   * duplicating it from the other side. welby-functions#371 is what makes that ownership real: the
   * engine evaluates the band against the patient's PREVIOUS reading and writes a distinct Low
   * `vital_flags` document carrying the §4.1 clause and the actual gain. Before it, a sub-2lb gain
   * matched no rule and fell through to the tier-1 flag (`measure_functions.ts`, *"No weight flag
   * triggered, creating Tier 0 flag for normal values"*, `Tier.ONE`) — the same document the engine
   * writes when the patient LOSES weight, with `alert_variance: 0` and no clause.
   *
   * A bounded gain IS expressible as a rule, so do not read this as "the engine cannot do it":
   * `evaluation: 'bounded_gain'` (welby-functions#371), seeded as `weight_low_24h` / `weight_low_3d`
   * (welby-firestore#131). What a rule cannot carry ALONE is the LOWER bound — one operator and one
   * offset, so a bare `< 2` is true of a three-pound LOSS. That bound lives in the engine. Either
   * way it does not live here: this component does not own the reading.
   *
   * So the symptom gate keeps its clinical meaning without this component writing an alert: a
   * symptomatic Low finding still reaches the NCM as a task from here, while the reading itself is
   * the engine's Low flag.
   *
   * THAT IS TWO ARTEFACTS, NOT ONE, and the earlier claim of "one finding, one owner" was wrong
   * about it. After welby-functions#371 a symptomatic Low weight finding produces an MA-owned
   * `vital_flags` document AND an NCM-owned task from here — two documents, two owners, one clinical
   * event. It is the intended design and it is not double-alerting: the alert records that the
   * SCALE read a small gain and sits in the MA's queue for the measurement-accuracy check §4.1 row 1
   * asks the MA for, while the task records that the patient said they were SYMPTOMATIC and is the
   * NCM's work. Neither queue shows the other's row. What CHF-E2 removed was the real duplicate —
   * this component writing a second `vital_flags` row for the same reading, into the same queue, with
   * a question in the value column.
   *
   * **ONE TASK PER REASON.** A call that trips four conditions leaves four tasks, each named for
   * its own reason and carrying only that reason's body. This REPLACES the aggregate task the first
   * cut of CHF-E1 shipped, by decision of the product owner: the NCM triages, reassigns and closes
   * one reason at a time, and an aggregate task has a single `completed` flag for all of them — so
   * closing the reason they worked closed the three they had not. N rows for one call is the cost,
   * and it is the intended cost.
   *
   * The two paths remain a UNION, not an either/or, and every reason from either becomes a task:
   *
   * | Path | Covers | Produces |
   * |---|---|---|
   * | tiered (`collectRoutableFindings`) | fields carrying an `escalation` block | one task per finding: tier, clause, verbatim protocol text, captured value |
   * | legacy (`escalatingFields`) | every other tripped field | one task per field, carrying its `escalate_note` |
   *
   * THE SPLIT REACHES THE SOC GUIDE AT AN ONBOARDED CLINIC, AND NOTHING ELSE. It takes BOTH
   * conditions — see `writesOneTaskPerReason` — and each excludes a different way of over-reaching:
   *
   * | Call | Tasks |
   * |---|---|
   * | CHF at an onboarded clinic (Emory; + Kareo outside prod) | ONE PER REASON, tiered and legacy alike |
   * | CHF at any of the other 38 clinics | ONE aggregate, byte-identical to pre-BRD |
   * | COPD / T2DM / HTN / General, at ANY clinic including Emory | ONE aggregate, byte-identical to pre-BRD |
   *
   * The clinic condition alone was not enough, and the third row is why: the legacy path is not
   * gated by the toggle, so an Emory MA's COPD call tripping three fields would have gone from one
   * task to three on a guide that carries no part of this BRD. Those are the forms as they were,
   * and they stay as they were at every clinic. The clinic condition is still required for the
   * first row, because the CHF document is global — every clinic loads the same `chf_ma`.
   *
   * Gating the legacy path on `usesTieredEscalation` would have silenced the 25 CHF conditions that
   * declare `escalate_when` but no `escalation` block yet (chest pain, SOB at rest, ED visits, side
   * effects, distress). `routedIds` keeps one finding from doing both.
   *
   * Only `owner: 'rn'` findings become tasks. A Low finding HELD by the MA writes nothing at all —
   * it is documented in the generated note by `collectCapturedResponses()` and shown on-screen by
   * the auto-check plus the computed delta. That is what makes AC-4 hold by construction: nothing is
   * written, so nothing can leak into the NCM's queue.
   *
   * The roster is read ONCE per call and handed to every write. It is what resolves the assignee,
   * which is the same for every reason on the call, so N reads would be N chances to fail for one
   * answer — and the read is skipped entirely when there is nothing pending to write.
   */
  private raiseCareCallEscalations$(): Observable<unknown> {
    if (this.escalationInFlight) {
      return of(null);
    }
    if (this.careRole !== 'ma') {
      return of(null);
    }
    // THE PATIENT IS PINNED HERE, before anything asynchronous, and every decision below reads the
    // pinned value rather than the live service.
    //
    // Both halves matter. The note path guards the same hazard at `:668` and `:959` because
    // otherwise "it would land verbatim PHI from one patient into another patient's chart", and the
    // escalation path had no equivalent: it read `currentPatientServiceID` INSIDE the switchMap,
    // after `staffLanes$` (10s timeout). An MA finishing patient A's call, pressing Generate Note
    // and switching to patient B while the roster read was in flight wrote N tasks on B carrying
    // A's `finding_label` and `finding_value` — and `recordEscalationEventFromTask` mints
    // append-only `clinical_escalation_events` rows from them, which cannot be corrected.
    //
    // Refusing rather than redirecting to the pinned id, because the answers are A's and the roster,
    // assignee and clinic all have to be A's too — and `currentPatientService` is B's object by
    // then. Nothing is lost: no key is recorded, so going back to A and generating the note again
    // writes every reason.
    if (!this.canWriteToActivePatientNote()) {
      this.snackService.genericSnackBar(
        'Patient changed during generation — the escalation was not routed. Reopen the call and generate the note again.',
        ['warn-snackbar'],
        6000,
      );
      return of(null);
    }
    const patient = this.patientService.currentPatientService;
    const patientId = this.patientService.currentPatientServiceID;
    this.escalationInFlight = true;
    return this.careCallAlertsEnabledForClinic$(patient).pipe(
      switchMap((enabled) => {
        // A field carrying an `escalation` block is routed by the TIERED path; every other tripped
        // field keeps the legacy task path. The two are a UNION, not an either/or — see the doc
        // comment.
        //
        // Gate CLOSED: `routed` is empty, so the exclusion set is empty too and every tripped field
        // falls through to the legacy task exactly as it did before CHF-E1. Emptying the set is the
        // point — excluding a field the tiered path then declines to write would drop the
        // escalation on the floor.
        const routed = enabled ? this.collectRoutableFindings() : [];
        const routedIds = new Set(routed.map((finding) => `${finding.sectionId}.${finding.fieldId}`));
        // Only `owner: 'rn'` escalates. A Low finding held by the MA is documented, never routed.
        const tiered = routed.filter((finding) => finding.owner === 'rn');
        const legacy = this.legacyEscalationReasons(routedIds);
        // Reasons this capture already wrote a task for are dropped HERE, before the roster read: a
        // retry after a partial failure must not re-read the directory for work it will not do.
        const reasons = this.escalationReasons(tiered, legacy, this.writesOneTaskPerReason(enabled));
        const pending = reasons.filter(
          (reason) => !this.writtenEscalationKeys.has(this.escalationDedupKey(patientId, reason.key)),
        );
        // Findings that resolved to `owner: 'ma'` — documented on the note, routed nowhere by
        // design. Counted HERE, above the guard, because EVERY outcome below has to be able to name
        // them. A call that holds a Low band and ALSO trips anything else — chest pain, SOB at rest,
        // an ED visit, any of the legacy conditions — has `pending.length > 0`, takes the write path
        // and used to report only 'Escalation task created for the NCM', which invites the reading
        // that everything on the form routed. That is the same defect as the silence it replaced,
        // in a worse shape: silence at least did not assert anything.
        const heldByMa = routed.length - tiered.length;
        // No roster read on an ordinary call: it is fetched only when there is something to route.
        if (pending.length === 0) {
          this.reportNothingRouted(reasons.length, heldByMa);
          return of(null);
        }
        return this.staffLanes$(patient).pipe(
          switchMap((lanes) => {
            const assigneeId = this.resolveNcmAssignee(patient, lanes);
            // Still ONE message, now that there are N tasks: a missing assignee or patient id fails
            // every reason for the same reason, and naming each would be four snackbars carrying one
            // sentence. Nothing is reset — no key was recorded, and `finalize` clears the flag.
            if (!patientId || !assigneeId) {
              this.snackService.genericSnackBar(
                this.rosterUnavailable
                  ? 'Could not reach the provider directory — the escalation was not routed. Try again.'
                  : 'Escalation needed but no NCM found to assign',
                ['error-snackbar'],
                6000,
              );
              return of(null);
            }
            return this.writeEscalationTasks$(
              pending,
              patientId,
              assigneeId,
              patient?.client_responsible_id,
              heldByMa,
              reasons.length - pending.length,
            );
          }),
        );
      }),
      // Cleared however the run ends, so a failed or half-written call can be retried by generating
      // the note again. What prevents duplicates is `writtenEscalationKeys`, not this flag.
      finalize(() => {
        this.escalationInFlight = false;
      }),
    );
  }

  /**
   * Whether THIS call writes one task per reason, or the one aggregate task it always used to.
   *
   * TWO conditions, because either one alone over-reaches:
   *
   * - the CLINIC carries `isChfCareCallAlertsEnabled` AND the CALLER is in the canary (`enabled`,
   *   both already re-read at write time — see `careCallAlertsEnabledForClinic$`). The `chf_ma`
   *   document is global and carries no clinic and no user, so without this every MA at every
   *   clinic on the platform gets the split the moment the seed lands.
   * - the GUIDE declares tiered escalations at all. This is what keeps the COPD, T2DM, HTN and
   *   General guides on the aggregate task AT EVERY CLINIC, Emory included: they carry no part of
   *   BRD-CLIN-2026-006, their MAs and NCMs never agreed to it, and an onboarded clinic running one
   *   of them is running the form as it was. There is no `usesTieredEscalation` flag to read —
   *   carrying an `escalation` block IS the property, so it is asked of the document directly.
   *
   * Read off the SCOPED document (`scopeGuideToClinic` ran before `buildForm`), which is the same
   * object every other escalation decision on this call is made from.
   */
  private writesOneTaskPerReason(enabled: boolean): boolean {
    return (
      enabled && (this.guide?.sections ?? []).some((section) => (section.fields ?? []).some((field) => !!field.escalation))
    );
  }

  /**
   * Gate for the tiered path: this CLINIC **and** this MA (BRD-CLIN-2026-006 is Emory's SOC, run by
   * four of Emory's MAs — not the platform's, and not all of Emory's).
   *
   * The guide document `welby_configuration/care_call_guides/guides/chf_ma` is GLOBAL — it carries
   * no clinic and no user anywhere — so without this check the §4.1 content applies to every MA at
   * every clinic the moment the seed lands.
   *
   * TWO DOCUMENTS, BOTH REQUIRED, because each answers a question the other cannot:
   *
   * - `clients/{id}.featureToggles.isChfCareCallAlertsEnabled` — has this CLINIC agreed to the SOC.
   * - the canary document's `user_ids` (`CHF_CARE_CALL_CANARY_DOC`) — is this CALLER one of the MAs it was
   *   rolled out to. Added 2026-09-08 on Luis Mata's narrowing: *"El canario de Emory es mas
   *   reducido todavia Yvette Paz, Jennifer Arney, Alana Bessera, Kyla Arguilez"*. Emory's other
   *   MAs keep the form exactly as it was, which a per-clinic toggle cannot express.
   *
   * `forkJoin` rather than a short-circuit, so the two reads are independent and neither ordering
   * can make one gate depend on the other. Both sources complete (`take(1)`) and swallow their own
   * errors into `false` — without that this never emits and the guide never loads.
   *
   * FAILS CLOSED on a patient with no clinic, a caller with no `user_id`, a missing canary document
   * and a read error on either. Every one of those is the pre-BRD form.
   *
   * Takes the patient rather than reading `currentPatientService`, so the caller can PIN it: at
   * write time the escalation flow has already fixed which patient this call belongs to, and a gate
   * that re-read the live service could answer for a different clinic than the one being written to.
   */
  private careCallAlertsEnabledForClinic$(patient: Patient | null | undefined): Observable<boolean> {
    const clientId = patient?.client_responsible_id;
    if (!clientId) {
      return of(false);
    }
    return forkJoin([
      this.clientsService.isCareCallAlertsEnabled$(clientId),
      this.careCallGuideService.isCareCallCanaryUser$(this.authService.user?.user_id),
    ]).pipe(map(([clinicOnboarded, callerInCanary]) => clinicOnboarded && callerInCanary));
  }

  /**
   * ONE TASK PER REASON, written in form order, each independently retryable.
   *
   * | Path | Covers | Produces |
   * |---|---|---|
   * | tiered (`collectRoutableFindings`) | fields carrying an `escalation` block | one task: category, answer, tier, clause, verbatim protocol text, required action |
   * | legacy (`escalatingFields`) | every other tripped field | one task: section, question, `escalate_note` |
   *
   * SEQUENTIAL on purpose — `concatMap`, not `forkJoin`. Each reason is a POST to
   * `/core/patients/{id}/clinical-tasks`; a call tripping ten conditions would otherwise open ten
   * concurrent writes for one patient, and the order the NCM's queue shows them in would be
   * whichever the backend finished first. This way it is the order the MA filled the form.
   *
   * PARTIAL FAILURE IS A REAL OUTCOME NOW, and it is reported as one. One failing POST does NOT
   * abort the rest — `catchError` per reason, because reason 2 failing is no argument for dropping
   * reasons 3 and 4. Each success records its key, so generating the note again writes only the
   * remainder. That is why this cannot be a `forkJoin` with a single error path either.
   *
   * `priority` IS derived from the tier now, one tier per task — see `TASK_PRIORITY_BY_TIER`. It was
   * pinned at `'high'` while every reason shared one aggregate task, and left there on the argument
   * that the §4.6 `task-prioritization` job (acuity -> days overdue -> next due date) rewrites it
   * anyway. That argument was wrong about the part the NCM sees: the value written HERE is the chip
   * on the row from the moment the MA generates the note until that job's next run, which is the
   * window the escalation is actually acted on. A Moderate finding spent it reading High.
   *
   * The job still overwrites it, and still reads neither the tier nor the type — measured on dev
   * 2026-09-10, its 08:00:22Z run put a `Weight gain >=3 lbs over 3 days (Moderate tier)` task at
   * `'low'`, on an unassessed patient whose task was not yet overdue. Making it respect the tier of
   * a `care_call_escalation` task is a welby-micro-service change and is NOT in this component's
   * reach; it is tracked separately. Deriving the tier correctly here is the half that is.
   */
  private writeEscalationTasks$(
    reasons: EscalationReason[],
    patientId: string,
    assigneeId: string,
    clientId: string | undefined,
    heldByMa = 0,
    alreadyWritten = 0,
  ): Observable<unknown> {
    // Both annotations are load-bearing: `addClinicalTask` is an untyped `http.post` returning
    // `Observable<Object>`, and without them this chain infers `unknown` rather than `boolean[]`.
    const written$: Observable<boolean[]> = from(reasons).pipe(
      concatMap((reason): Observable<boolean> => {
        // Re-checked before EVERY write, not once at the start. The writes are sequential and each
        // is a network round trip, so the active patient can change between reason 2 and reason 3 —
        // and `patientId` is pinned, so continuing would keep writing patient A's findings while
        // the MA reads patient B's chart. Stopping is safe in a way that continuing is not: no key
        // is recorded for a reason that was not written, so the remainder is still retryable.
        if (!this.canWriteToActivePatientNote()) {
          return of(false);
        }
        return this.patientService.addClinicalTask(this.escalationTask(reason, patientId, assigneeId, clientId)).pipe(
          // Recorded only on success: an unwritten reason has to stay retryable.
          //
          // KEYED ON THE PATIENT. The reason keys are `sectionId.fieldId` taken off the SHARED
          // `chf_ma` document, so they are identical for every patient: patient A's call recording
          // `symptoms.swelling` would otherwise make the same field on patient B's call read as
          // already written, and B's escalation would be dropped with no task and no snackbar.
          tap(() => this.writtenEscalationKeys.add(this.escalationDedupKey(patientId, reason.key))),
          map(() => true),
          catchError(() => of(false)),
        );
      }),
      toArray(),
    );
    return written$.pipe(
      tap((outcomes: boolean[]) =>
        this.reportEscalationOutcome(outcomes.filter(Boolean).length, outcomes.length, heldByMa, alreadyWritten),
      ),
    );
  }

  /**
   * One reason's task. `type` is untouched — the §4.6 job groups on it, not on the name.
   *
   * The audit stamp rides on the SAME document, which is what makes the compliance row possible
   * without a change in welby-functions or welby-micro-service: the tasks endpoint takes a plain
   * `ClinicalTask` with no DTO whitelist and persists the body verbatim (`docRef.set(task)`), and
   * `recordEscalationEventFromTask` already watches the collection. Absent on the aggregate task.
   */
  private escalationTask(
    reason: EscalationReason,
    patientId: string,
    assigneeId: string,
    clientId: string | undefined,
  ): ClinicalTask {
    return {
      patient_id: patientId,
      assignee_id: assigneeId,
      creator_id: this.authService.user?.user_id,
      client_id: clientId,
      task_name: this.escalationTaskName(reason.label),
      task_description: reason.body,
      // `?? 'high'` is the legacy and aggregate path, unchanged: neither carries a tier.
      priority: reason.priority ?? 'high',
      type: 'care_call_escalation',
      due_date: new Date(),
      completed: false,
      ...(reason.audit ?? {}),
    };
  }

  /**
   * One LEGACY finding as a task body — `findingBlock()`'s shape, with only what such a field has.
   *
   * The body it replaces was the task's own NAME again: `Symptom Screen: Shortness of breath lying
   * flat (orthopnea)?`, and nothing else. It did not say what the patient answered, which for a
   * question ending in '?' is the entire content of the finding — an NCM reading that row could not
   * tell a 'Yes' from an 'Only on two pillows' (Marcela Malaver, 2026-09-10: "quiero que cada
   * notificación indique qué cambió, qué significa y qué se recomienda").
   *
   * | Marcela's question | line | when it is absent |
   * |---|---|---|
   * | what changed | `<question>: <answer>` | never — the answer is why the field tripped |
   * | what it means | `What this means: <escalate_meaning>` | the seed declares no meaning |
   * | what is recommended | `Recommended: <escalate_note>` | the seed declares no note |
   *
   * ## Why "what it means" is seeded text and not a tier
   *
   * For a TIERED finding that question is answered by `Tier:` and `Protocol text:`, both read off an
   * `escalation` block that a legacy field by definition does not carry. (It was three lines until
   * the `Protocol clause:` id came out of the body — camiloco, PR review, 2026-09-11.) The
   * first attempt at this method wrote nothing for it, which is what Marcela saw on the orthopnea
   * task: `Shortness of breath lying flat (orthopnea)?: Yes` and no more.
   *
   * The fix is NOT to derive a tier here. A tier now decides the task's priority
   * (`TASK_PRIORITY_BY_TIER`), so choosing one in this method would invent a queue position for a
   * finding no protocol has graded — and §4.1's clause catalog has no row for any of these symptoms
   * (it grades weight, vitals, diuretic dose change, edema and adherence, nothing else), so there is
   * not even a clause to quote. `escalate_meaning` is an EXPLANATION seeded per field: it answers the
   * NCM's question without asserting an acuity grade, and it leaves routing and priority exactly
   * where they were on this path.
   *
   * Both lines are omitted rather than filled in when the seed is silent. A task that says
   * `What this means: ` with nothing after it is worse than one that does not raise the question,
   * and a plausible sentence generated here would be an unsigned clinical claim in a record kept for
   * six years.
   *
   * All 23 of `chf_ma` v2.16's legacy fields are annotated by
   * `maintenanceScripts/sprint-9-11-26/findingContextAnnotations.js` in welby-firestore, which also
   * fills the `escalate_note` the seven bare ones never had (`sob_at_rest`, `sob_lying_flat`,
   * `more_pillows_to_sleep`, `new_worsening_fatigue`, `palpitations_racing_heartbeat`,
   * `patient_appears_in_distress`, `safety_close.escalation_required`). The other four guides are not
   * annotated on purpose: they declare no tiered escalation anywhere, so `writesOneTaskPerReason()`
   * is false for them and this block never renders — the seeded text would be data nothing reads.
   *
   * The section label leads, where a tiered block leads with `escalation.category`: it is the same
   * kind of thing — where on the form this came from — and it keeps the two bodies reading alike in
   * a queue that interleaves them.
   */
  private legacyFindingBlock(section: GuideSection, field: GuideField, label: string, value: string): string[] {
    // The answer line is guarded like the two below it, and for the same reason: an EMPTY value
    // renders `Question: ` with nothing after it — the ragged line this docblock argues against
    // (juana98, PR review, 2026-09-10).
    //
    // A legacy field can trip with no answer to print: `formatCapturedValue` reports an unticked
    // checkbox as unanswered (`isAnswered(false)` is false), so a field seeded
    // `escalate_when: { equals: false }` escalates on exactly the state that yields ''. None of
    // #139's 23 fields trip on `false` today, which makes this LATENT rather than live — but the
    // seed is a document that can be edited straight in Firestore, and the cost of the guard is one
    // ternary against a task body kept for six years.
    //
    // The label alone, not `<label>: (no answer)`: what the field is IS the finding in that shape,
    // and inventing a parenthetical would put words in an append-only record that no seed authored.
    //
    // The section line is dropped when the label already opens with it. `fieldLabel` synthesizes
    // `<section.label> — <id>` for a blank-labelled field, which rendered `Weight Check` above
    // `Weight Check — other_notes: Yes` — the section named twice in a two-line body. The AGGREGATE
    // `line` has the same shape and is deliberately NOT touched: it is byte-frozen for the 38
    // clinics outside the canary.
    const answer = value ? `${label}: ${value}` : label;
    const lines = answer.startsWith(`${section.label} — `) ? [answer] : [section.label, answer];
    if (field.escalate_meaning) {
      lines.push(`What this means: ${field.escalate_meaning}`);
    }
    if (field.escalate_note) {
      lines.push(`Recommended: ${field.escalate_note}`);
    }
    return lines;
  }

  /**
   * The dedup key for one reason on one patient.
   *
   * The reason key alone is not unique: it is `sectionId.fieldId` from the GLOBAL guide document, so
   * every patient's call produces the same keys. `patientId` is the pinned one, never the live
   * service, so the key cannot drift mid-run either.
   *
   * `'unknown'` for a missing id rather than dropping the prefix: it keeps the two halves of the key
   * unambiguous (a patient id cannot contain `|`), and a run with no patient id writes nothing
   * anyway — the assignee guard returns first.
   */
  private escalationDedupKey(patientId: string | null | undefined, reasonKey: string): string {
    return `${patientId ?? 'unknown'}|${reasonKey}`;
  }

  /**
   * `Care Call Escalation Review — <reason>`.
   *
   * The NAME has to carry the reason now. Four tasks all called `Care Call Escalation Review` are
   * four indistinguishable rows in the NCM's queue, and the body is not a reliable second chance:
   * the task-detail dialog binds `task?.description` while this writes `task_description`, so the
   * body renders blank there today. The PREFIX is kept byte-identical, so every
   * `care_call_escalation` task already in the queue still matches a name search, and the §4.6 job
   * is indifferent either way — it groups on `type`.
   *
   * Truncated because the reason is a seeded QUESTION, not a code: the longest escalating label in
   * `chf_ma` v2.14 runs 75 characters ('SBP ≥180 OR DBP ≥120 with symptoms (headache, vision
   * changes, chest pain)'), and a queue row is not the place to render a paragraph. An empty label
   * yields the bare pre-BRD name rather than a dangling dash.
   */
  private escalationTaskName(label: string): string {
    const reason = (label ?? '').trim();
    if (!reason) {
      return CareCallGuideBaseComponent.ESCALATION_TASK_NAME;
    }
    const max = CareCallGuideBaseComponent.TASK_NAME_REASON_MAX_LENGTH;
    const clipped = reason.length > max ? `${reason.slice(0, max - 1).trimEnd()}…` : reason;
    return `${CareCallGuideBaseComponent.ESCALATION_TASK_NAME} — ${clipped}`;
  }

  /**
   * What the MA is told: ONE snackbar for the whole call, whatever the reason count.
   *
   * The single-reason wording is byte-identical to the aggregate task's, success and failure alike,
   * because for a one-reason call nothing about the outcome changed. The partial sentence names the
   * count and the remedy, because "escalation task created" after two of four is a lie the MA would
   * act on.
   *
   * The SUCCESS sentence also carries what did NOT route (`heldByMa`), because on this path a held
   * finding is otherwise unspeakable: a call that trips chest pain and holds a Low weight band
   * reports "Escalation task created for the NCM", and the MA has no way to tell that from a call
   * where everything on the form routed. That is the reading Marcela's report came from.
   *
   * The FAILURE sentences deliberately do not: they say something failed and name the remedy, which
   * cannot be misread as "everything routed", and the held clause would only compete with the
   * remedy. The retry that succeeds carries it.
   */
  private reportEscalationOutcome(written: number, total: number, heldByMa = 0, alreadyWritten = 0): void {
    if (written === total) {
      const created = total === 1 ? 'Escalation task created for the NCM' : `${total} escalation tasks created for the NCM`;
      this.snackService.genericSnackBar(
        `${created}${this.alreadyRoutedClause(alreadyWritten)}${this.heldByMaClause(heldByMa)}`,
        ['success-snackbar'],
        4000,
      );
      return;
    }
    if (written === 0) {
      // The clause rides on THIS branch too. Without it a retry that fails completely reads "Could
      // not create any of the NCM escalation tasks" on a call where some findings DID route earlier
      // — the same misreading `alreadyRoutedClause` exists to remove, in the failure shape, and the
      // shape where it matters most: the MA is deciding whether anything reached the NCM at all
      // (juana98, PR review, 2026-09-11, approving #1256 with this as the one case still silent).
      this.snackService.genericSnackBar(
        `${total === 1 ? 'Could not create the NCM escalation task' : 'Could not create any of the NCM escalation tasks'}${this.alreadyRoutedClause(
          alreadyWritten,
        )}`,
        ['error-snackbar'],
        6000,
      );
      return;
    }
    this.snackService.genericSnackBar(
      `Created ${written} of ${total} escalation tasks${this.alreadyRoutedClause(
        alreadyWritten,
      )} — generate the note again to create the rest`,
      ['error-snackbar'],
      6000,
    );
  }

  /**
   * The clause naming findings THIS CALL already routed on an earlier generation.
   *
   * Reported against welby-dev on 2026-09-10 (Andres Rodriguez) as the 48-hour band's task not being
   * created "when several are selected", while "selecting only the 48-hour validation" created it.
   * Both halves are true and the task was never lost. What happened is the regenerate:
   *
   * 1. the MA types today's weight and the 2-days-ago anchor, the §4.2 rapid band ticks on its own
   *    rule, and generating the note writes ONE task and records its key in `writtenEscalationKeys`;
   * 2. the MA then types yesterday's weight, the §4.1 Moderate band ticks beside it — the two are
   *    independent criteria, not grades of each other — and generating the note again writes the
   *    Moderate task and correctly SKIPS the rapid one, which is already in the NCM's queue.
   *
   * Step 2 then reported `Escalation task created for the NCM`, singular, with both boxes ticked on
   * the form in front of her and both findings named in the note. The only reading available is that
   * the 48-hour one did not make it — which is the same defect as `reportNothingRouted`'s silence
   * (Marcela Malaver, 2026-09-09), in the shape a PARTIAL regenerate takes: `reportNothingRouted`
   * covers the call where EVERY reason was already written, and nothing covered the call where some
   * were.
   *
   * The dedup itself is correct and is not touched: a second task for a finding already sitting in
   * the NCM's queue is a worse outcome than a confusing sentence. What was missing is the sentence.
   *
   * ONE snackbar, for the reason `heldByMaClause` documents — `MatSnackBar.open` replaces the
   * currently-open bar, so a second call would destroy the first within a frame. Ordered
   * created → already routed → held: what this generation did, then what the call had already done,
   * then what it deliberately did not do.
   */
  private alreadyRoutedClause(alreadyWritten: number): string {
    if (alreadyWritten <= 0) {
      return '';
    }
    return alreadyWritten === 1
      ? ' — 1 other finding was already routed earlier in this call'
      : ` — ${alreadyWritten} other findings were already routed earlier in this call`;
  }

  /**
   * The held-finding clause appended to whatever else this call has to say — `''` when nothing was
   * held, so the ordinary sentence is byte-identical to the one that shipped.
   *
   * ONE SENTENCE, NOT TWO SNACKBARS, and that is a constraint rather than a preference:
   * `SnackService.genericSnackBar` is `MatSnackBar.open`, which DISMISSES the currently-open bar and
   * opens the new one in its place. Two calls in a row means the first is replaced within a frame —
   * the MA reads the second and never sees the first. So on a call that both routes and holds, a
   * separate held snackbar would either be the one destroyed or the one that destroys the
   * task-created confirmation. Neither is acceptable, and the outcome has to be legible in a single
   * message.
   *
   * SHORTER than the standalone held sentence on purpose. Standalone, holding IS the news and the
   * message has to teach why — 'it stays with the MA, so no NCM task was created'. Appended, the
   * news is the task that WAS created and this is the qualifier on it, so it names the count and the
   * owner and stops. Both must survive being quoted back in Slack, which is how this reached us.
   */
  private heldByMaClause(heldByMa: number): string {
    if (heldByMa <= 0) {
      return '';
    }
    return heldByMa === 1
      ? ' — 1 low-tier finding is documented on the note and stays with the MA'
      : ` — ${heldByMa} low-tier findings are documented on the note and stay with the MA`;
  }

  /**
   * What the MA is told when the escalation path writes NOTHING — which until now was nothing at all.
   *
   * Reported by Marcela Malaver on 2026-09-09 against a §4.1 Low weight band: *"La nota se generó y
   * se completó sin problemas, pero ni en el paciente ni a la RN llegó la tarea"*. The product was
   * behaving exactly as BRD-CLIN-2026-006 §3.2 requires — a Low finding with the symptom gate closed
   * is HELD by the MA and routes nowhere — but the form said so in no way whatsoever. The MA sees a
   * band tick, a red-free row, a note that generates cleanly, and an empty NCM queue, and the only
   * reading available to her is "the escalation is broken". Silence is not a safe way to report a
   * deliberate non-escalation on a clinical form.
   *
   * TWO cases, and the ORDINARY one stays silent. A call where nothing tripped must not snackbar
   * "nothing to escalate" — that is most calls, and a confirmation nobody asked for on every note is
   * how a message stops being read. So this speaks only when there WAS something and it went nowhere:
   *
   * - `alreadyWritten > 0` — every reason on this call already has its task, because the MA generated
   *   the note a second time. Previously identical to the do-nothing path, so a regenerate after a
   *   successful run looked like a run that failed. Reported as success, because it is one.
   * - `heldByMa > 0` — routable findings resolved to `owner: 'ma'` (the Low bands, gate closed). Named
   *   as a decision with its reason, not as an outcome: the MA has to be able to tell "held" from
   *   "lost", and the sentence has to survive being quoted back in Slack.
   *
   * `warn-snackbar` rather than `error-snackbar` for the held case: nothing failed, and dressing a
   * correct outcome as an error would push MAs to escalate around it.
   */
  private reportNothingRouted(alreadyWritten: number, heldByMa: number): void {
    if (alreadyWritten > 0) {
      const already =
        alreadyWritten === 1
          ? 'The escalation task for this call was already created'
          : `All ${alreadyWritten} escalation tasks for this call were already created`;
      // The held clause rides along here for the same reason it rides on the success sentence: on a
      // regenerate this branch returns before the held check, so a finding held beside an
      // already-written task would go unnamed on every regenerate of that call.
      this.snackService.genericSnackBar(`${already}${this.heldByMaClause(heldByMa)}`, ['success-snackbar'], 4000);
      return;
    }
    if (heldByMa > 0) {
      this.snackService.genericSnackBar(
        heldByMa === 1
          ? 'Low-tier finding documented on the note — it stays with the MA, so no NCM task was created'
          : `${heldByMa} low-tier findings documented on the note — they stay with the MA, so no NCM task was created`,
        ['warn-snackbar'],
        6000,
      );
    }
  }

  /**
   * Every reason this call escalated for, tiered first, in form order — or the ONE aggregate reason,
   * for a clinic outside the canary.
   *
   * Tiered reasons carry the guide revision (AC-6) and legacy ones deliberately do not, so a lone
   * legacy reason's body is byte-identical to the body the aggregate task produced for it.
   */
  private escalationReasons(
    tiered: ResolvedFinding[],
    legacy: LegacyEscalationReason[],
    perReason: boolean,
  ): EscalationReason[] {
    if (!perReason) {
      return this.aggregateEscalationReason(tiered, legacy);
    }
    return [
      ...tiered.map((finding) => ({
        key: `${finding.sectionId}.${finding.fieldId}`,
        label: finding.label,
        body: this.escalationBody(this.findingBlock(finding), true),
        // From the DECLARED tier, never from `symptomatic`. An open symptom gate moves a Low finding
        // to the RN's queue; it does not make it a Moderate one — under §4.1 the NCM is the one who
        // "reviews and assesses for acuity upgrade to Moderate". Deriving a higher priority here
        // would make the platform perform that upgrade silently, with the task body still reading
        // 'Tier: Low'.
        priority: CareCallGuideBaseComponent.TASK_PRIORITY_BY_TIER[finding.escalation.tier],
        audit: this.auditStamp(finding.owner, finding.label, finding.value, {
          symptomatic: finding.symptomatic,
          clause: finding.escalation.clause,
          clauseText: this.resolveClauseText(finding.escalation),
        }),
      })),
      ...legacy.map((reason) => ({
        key: reason.key,
        label: reason.label,
        // `reason.block`, not `reason.line`: the line is the AGGREGATE body's rendering and stays
        // frozen for the clinics outside the canary — see `LegacyEscalationReason.block`.
        body: this.escalationBody(reason.block, false),
        // `owner: 'rn'` is the ROUTING, not a claim about the rule: a legacy field declares no
        // owner, and this task is assigned to the patient's RN-lane provider — so the §4.1
        // MA-to-NCM handoff the trigger derives from it is what actually happened. No clause and no
        // symptom gate exist on this path, and those fields are omitted rather than invented: a
        // null in an append-only compliance row is a fact, a guess is not.
        audit: this.auditStamp('rn', reason.label, reason.value),
      })),
    ];
  }

  /**
   * The clause whose task welby-functions anchors the §4.2 row 9 deadlines on.
   *
   * Mirrors `DOSE_ESCALATION_CLAUSE` in welby-functions `vitals/dose-escalation-followups.ts`. The id
   * is the contract between the two repos; it is restated here rather than imported because they
   * share no package, the same way the clause catalog is mirrored.
   */
  private static readonly DOSE_ESCALATION_CLAUSE = 'BRD-CLIN-2026-006#4.2-dose-escalation-monitoring';

  /** How far back an "approximate date of the change" is believed. Beyond this it reads as a typo. */
  private static readonly MAX_DOSE_CHANGE_AGE_DAYS = 90;

  /**
   * The date the dose actually changed, for the one finding whose deadlines are counted from it.
   *
   * §4.2 row 9 requires the PCP notified "within 24 hours of the titration or deployment event". The
   * event is the DOSE CHANGE, and the guide captures it — `medications.diuretic_dose_change_date`,
   * gated on the same answer that raises this finding, so whenever row 9 fires the date was askable.
   * Without this the deadline was counted from `create_date`, which is when the call was written up:
   * a Wednesday call about a Monday change started the 24-hour clock on Wednesday.
   *
   * REFUSES RATHER THAN GUESSES, and the two directions are not symmetric:
   *
   * - A FUTURE date is rejected outright. Anchoring there would push a regulatory deadline later,
   *   which is the one failure mode that must not be reachable by a typo.
   * - An implausibly OLD date is rejected too. Overdue-on-arrival is the safe direction, so this is
   *   the softer call — but a mistyped year would render an obligation as missed when it was not,
   *   in a row nobody can correct afterwards.
   *
   * Either way it returns undefined and the existing `create_date` fallback stands, which is exactly
   * today's behaviour. This can improve the anchor; it cannot break it.
   *
   * WHICH TIMEZONE, asked in review on #1263. The datepicker is Angular Material's `MatNativeDateModule`,
   * so the value is local midnight in the BROWSER's zone — the MA's, not the patient's. welby-functions
   * deliberately uses the PATIENT's zone for §4.3's weekend logic (`isWeekendInZone`), so the two are
   * not the same convention, and an MA in PT writing up an ET patient shifts this anchor by three
   * hours. Left as the MA's zone: the answer is a date the MA typed, a day rather than an instant, and
   * re-interpreting it in the patient's zone would be inventing precision the question never had.
   * Noted rather than resolved — it is worth Emory's view on a 24-hour window.
   *
   * ## IT REACHES THREE CONSUMERS, NOT ONE — know this before widening the bounds
   *
   * `escalation_occurred_at` is an EXISTING field in the escalation-stamp contract, not one invented
   * here, and welby-functions reads it in two places:
   *
   * 1. `dose-escalation-follow-up-tasks.ts` — the obligation deadlines. The intended target.
   * 2. `record-escalation-event.ts` — the `clinical_escalation_events` row's `occurred_at`, which by
   *    that file's own comment "drives retain_until and the ordering of every index". So the date
   *    also sets when an append-only compliance row's 6-year retention expires.
   *
   * Accepted deliberately (Valentina, 2026-09-14): one field, one meaning — when the event happened —
   * with all three consumers consistent, because retention running from the clinical event is more
   * defensible than retention running from whenever someone typed the call up.
   *
   * That is also the real reason the bounds below are strict. They are not protecting a task due
   * date; they are keeping a typo out of a retention clock on a row nobody can correct afterwards.
   */
  private doseChangeOccurredAt(clause: string | undefined): Date | undefined {
    if (clause !== CareCallGuideBaseComponent.DOSE_ESCALATION_CLAUSE) {
      return undefined;
    }
    const control = this.guideForm?.get('medications.diuretic_dose_change_date');
    if (!control) {
      // A BLANK ANSWER AND A MISSING FIELD BOTH YIELD undefined, and only one of them is fine. If the
      // seed ever stops shipping `diuretic_dose_change_date`, every deadline silently reverts to the
      // write-up time and nothing downstream can tell that from an MA who left the date empty — the
      // whole feature would look healthy. Say so once, here, rather than let it be invisible.
      // Raised in review on #1263; welby-firestore's own seeds make the same distinction.
      console.warn(
        '[care-call-guide] §4.2 row 9 fired but medications.diuretic_dose_change_date is not in the guide — ' +
          'the PCP deadline will fall back to the call time. Check the chf_ma seed.',
      );
      return undefined;
    }
    const raw = control.value ?? null;
    if (raw === null || raw === undefined || raw === '') {
      return undefined;
    }
    // `as unknown` first: `GuidePrimitive` does not admit Date, but a date control can hand one back
    // at runtime, so the check has to be made without the compiler ruling it impossible.
    const value = raw as unknown;
    const at = value instanceof Date ? new Date(value.getTime()) : new Date(String(value));
    if (!Number.isFinite(at.getTime())) {
      return undefined;
    }
    const now = Date.now();
    if (at.getTime() > now) {
      return undefined;
    }
    const ageDays = (now - at.getTime()) / (24 * 60 * 60 * 1000);
    if (ageDays > CareCallGuideBaseComponent.MAX_DOSE_CHANGE_AGE_DAYS) {
      return undefined;
    }
    return at;
  }

  /**
   * The audit stamp for one reason (BRD NFR-5.1) — see `CareCallAuditStamp` for the contract.
   *
   * `finding_value` is the captured ANSWER, never the label. Both fields holding the question reads
   * as "we asked X" with no record of what was answered, and it lands in an append-only row where
   * it cannot be corrected — the same defect `findingValue`'s own comment records.
   *
   * `guide_version` is required for the row's `question_ref`: the trigger builds
   * `care_call_guides/<diagnosis>@<version>` and yields NULL unless BOTH are present, so a call
   * captured against an unresolved guide revision would produce a row that cannot say which
   * question set was asked. Empty string rather than a fabricated version if it is missing.
   */
  private auditStamp(
    owner: string,
    label: string,
    value: string,
    tiered?: { symptomatic: boolean; clause?: string; clauseText?: string },
  ): CareCallAuditStamp {
    const doseChangeAt = this.doseChangeOccurredAt(tiered?.clause);
    return {
      source: 'care_call_guide',
      owner,
      captured_by: this.authService.user?.user_id ?? null,
      guide_diagnosis: this.resolvedDiagnosis,
      guide_version: this.guide?.version ?? '',
      finding_label: label,
      finding_value: value,
      ...(tiered ? { symptomatic: tiered.symptomatic } : {}),
      ...(tiered?.clause ? { protocol_clause: tiered.clause } : {}),
      ...(tiered?.clauseText ? { protocol_clause_text: tiered.clauseText } : {}),
      ...(doseChangeAt ? { escalation_occurred_at: doseChangeAt } : {}),
    };
  }

  /**
   * The clause's wording, CATALOG first and the guide document second.
   *
   * Extracted so the task BODY and the audit STAMP resolve it identically. Duplicating the fallback
   * is exactly the drift `findingBlock`'s table warns about: an id present in only one source must
   * still resolve, so a second copy that dropped either side would put clause text on the task and a
   * null in the compliance row for the same finding.
   *
   * THE ORDER WAS DOCUMENT-FIRST AND IS NOW CATALOG-FIRST, changed deliberately. `formatProtocolClause`
   * — what the NCM reads in the alerts table — has always been catalog-first, so once a clause existed
   * in BOTH sources the same finding rendered two different wordings depending on the surface. Row 9
   * made that live: the alerts table showed the BRD sentence while this path kept the seeded
   * paraphrase, and that paraphrase reached the append-only `clinical_escalation_events` row.
   *
   * Measured against the deployed `chf_ma` (v2.19) before flipping it rather than reasoned about: 10
   * fields carry a clause present in both sources, SEVEN of them byte-identical. The three that
   * differ — `weight_gain_moderate_24h`, `weight_gain_moderate_3d` and
   * `diuretic_dose_change_direction` — differ in exactly one respect: the seeded copy still names a
   * BRD section ("in Section 4.3", "Section 4.2 - ") and the catalog copy does not. So the flip does
   * one thing, which is finish removing section numbers from text a clinician reads.
   *
   * The fallback to `clause_text` is NOT vestigial: nine fields cite a clause the catalog does not
   * hold, and dropping it would send every one of them to the NCM as a bare id.
   */
  private resolveClauseText(escalation: GuideEscalation): string | undefined {
    // The stored fallback is scrubbed of its leading BRD section reference; catalogued text never has
    // one. This path feeds the task body AND the `clinical_escalation_events` stamp, so the nine
    // seeded clauses the catalog cannot yet hold stop putting "Section 4.2 - " in front of an NCM.
    const seeded = escalation.clause_text ? stripSeededSectionPrefix(escalation.clause_text) : undefined;
    return findProtocolClause(escalation.clause)?.text || seeded || undefined;
  }

  /**
   * The pre-BRD write, preserved exactly, for a clinic that never onboarded to the SOC.
   *
   * ONE task, the bare `Care Call Escalation Review` name, one body listing every tripped field
   * under the pre-BRD header, one snackbar. In practice `tiered` is empty here — the gate that
   * selects this path also stops `collectRoutableFindings` from being called — so there is no tier,
   * no clause and no `Guide:` line to place, and no "Other findings from this call" heading either:
   * with nothing above them, those lines ARE the body, which is what they were before CHF-E1.
   *
   * IT IS STILL TAKEN, not asserted. That emptiness is held by two separate predicates in two
   * methods (`writesOneTaskPerReason` asks the document for an `escalation` block;
   * `collectRoutableFindings` requires one per field), and nothing makes them move together. Widen
   * the second one later — to `escalate_when`, say — and a tiered finding would arrive here and be
   * dropped with no task, no alert and no error, which is the one outcome this component must never
   * produce. Folding it into the body costs a `map` and removes the coupling; the byte-identical
   * pre-BRD body is unchanged whenever `tiered` is empty, which is every call today.
   *
   * This is what keeps the COPD, T2DM, HTN and General guides — at EVERY clinic, Emory included —
   * and CHF at the other 38, unchanged by the per-reason split. The escalation still routes, in the
   * shape their NCMs and their open tasks already have.
   *
   * NO AUDIT STAMP, deliberately. NFR-5.1 is a requirement of BRD-CLIN-2026-006, which is Emory's
   * SOC, and the alert path that fed `clinical_escalation_events` was gated by the same toggle — so
   * a non-onboarded clinic's escalations were never audited and are not being dropped from anything
   * here. Stamping this task would also be a lie in shape: the trigger records ONE form answer per
   * row, and this task holds every reason on the call.
   */
  private aggregateEscalationReason(tiered: ResolvedFinding[], legacy: LegacyEscalationReason[]): EscalationReason[] {
    if (legacy.length === 0 && tiered.length === 0) {
      return [];
    }
    // Tiered first, then legacy — the same order the per-reason path lists them in, so a body
    // assembled here reads like the tasks it would otherwise have been split into.
    // `reduce`, not `flatMap`: the project's `lib` target predates ES2019.
    const tieredLines = tiered.reduce<string[]>((lines, finding) => [...lines, ...this.findingBlock(finding)], []);
    const reasonLines = [...tieredLines, ...legacy.map((reason) => reason.line)];
    return [
      {
        key: CareCallGuideBaseComponent.AGGREGATE_REASON_KEY,
        // Empty on purpose: `escalationTaskName` then yields the unsuffixed pre-BRD name.
        label: '',
        // The revision is stamped only when a tiered finding actually reached here (AC-6 applies to
        // those), which keeps the legacy-only body — every real call on this path — byte-identical.
        body: this.escalationBody(reasonLines, tiered.length > 0),
      },
    ];
  }

  /**
   * ONE reason's task body: what was asked, what the MA answered, which tier it is and what the
   * protocol REQUIRES — `Protocol text:`, the clause's own wording.
   *
   * It no longer NAMES the clause. The `Protocol clause: <id>` line came out of the body, so the
   * §5.1 traceability that used to ride on the alert document now rides on the task DOCUMENT
   * (`protocol_clause`, `protocol_clause_text`), which is what `recordEscalationEventFromTask`
   * copies into `clinical_escalation_events`. That is a stronger place for it than a line of prose:
   * it is queryable, and nothing downstream reformats it (camiloco, PR review, 2026-09-11, on this
   * docstring still claiming the body carries the clause).
   *
   * BYTE-IDENTICAL FOR A LONE REASON, and that is the compatibility claim worth asserting rather
   * than assuming. The first line is the pre-BRD one; a single legacy reason produces exactly the
   * two-line body the aggregate task produced for it — no `Guide:` line, no blank line, no heading;
   * a single tiered reason produces exactly the header / `Guide:` / blank / block it produced.
   *
   * No `Reason N` prefix and no "Other findings from this call" heading survive: both existed only
   * to separate reasons that shared a body, and no body holds two reasons any more.
   */
  private escalationBody(reasonLines: string[], stampGuideRevision: boolean): string {
    const lines: string[] = ['Escalation triggered during the care call:'];
    // AC-6, now genuinely once per reason: the thresholds live in a seeded document that changes
    // between sprints, so a finding without the revision it was captured against cannot be
    // explained six weeks later. It rode on the alert's `guide_version` field before.
    //
    // The blank line is pushed for a tiered reason whether or not the revision resolves, because
    // that is what the aggregate body did — `lines.push('', ...block)` ran unconditionally.
    if (stampGuideRevision) {
      if (this.guide?.version) {
        lines.push(`Guide: ${this.resolvedDiagnosis} v${this.guide.version}`);
      }
      lines.push('');
    }
    lines.push(...reasonLines);
    return lines.join('\n');
  }

  /**
   * One reason's lines, which since the per-reason split are the whole of one task's body.
   *
   * `clause_text` prefers the web catalog and falls back to the guide document. BOTH directions are
   * load-bearing against the deployed document, which is why neither can be simplified away.
   *
   * MEASURED against `chf_ma` v2.19 — the old version of this table asserted the opposite of what the
   * document actually holds, having been written for v2.14 and never revisited:
   *
   * | Clause | On the guide doc | In the web catalog | Live source |
   * |---|---|---|---|
   * | the 10 catalogued ids (§4.1, §4.2 row 9, the two §4.3 titration rows) | `clause_text` ALSO present | present | the catalog |
   * | the other 9 seeded ids | `clause_text` present | absent — no rule cites them | the document |
   *
   * The catalog holds only clauses a rule cites, see its module header; everything else was seeded
   * with its text instead. Drop the catalog fallback and the catalogued findings fall back to a
   * seeded paraphrase that still names BRD sections; drop the document and nine findings reach the
   * NCM as a bare id. An id that resolves in NEITHER yields no text at all, on purpose — AC-5 is
   * enforced at seed time, and inventing wording here would hide a drifted catalog.
   *
   * The quote is the WHOLE clause, both halves, deliberately — the opposite of what the alerts table
   * does. There the row is one line in a dense queue and `formatProtocolClauseTrigger` shows only
   * the triggering condition; here the task IS the §5.1 record of why the NCM was called, so it
   * carries the protocol verbatim, `Required System Behavior` half included.
   *
   * Frozen once written: a task body is prose stored once, so a later wording correction cannot
   * re-render it. That is the right behaviour for §5.1 — the record is the wording that was in
   * force during the call, not today's.
   */
  private findingBlock(finding: ResolvedFinding): string[] {
    const escalation = finding.escalation;
    // The alert category the seed declares, which is the human-readable name the NCM already knows
    // from the alerts table ('Abnormal Labs', 'GDMT Tolerance').
    const category = escalation.category ?? 'Care Call';
    const lines = [
      category,
      `${finding.label}: ${finding.value}`,
      `Tier: ${CareCallGuideBaseComponent.TIER_LABELS[escalation.tier] ?? escalation.tier}`,
    ];
    // `Protocol clause: BRD-CLIN-2026-006#4.1-moderate-weight` USED TO SIT HERE, and it is gone for
    // the same reason as the note's citation: the id names an internal document, the NCM acting on
    // the task cannot open it, and it pushed the line that says what the protocol REQUIRES further
    // down the body (Andres Rodriguez, 2026-09-11).
    //
    // `Protocol text` stays — it is the clause's own wording, which is the part an NCM can act on,
    // and it names no document. And `protocol_clause` is still stamped on the task document itself,
    // so the compliance row keeps the id this line was displaying.
    const clauseText = this.resolveClauseText(escalation);
    if (clauseText) {
      lines.push(`Protocol text: ${clauseText}`);
    }
    // The BRD's required behaviour, as the seed declares it ('same_day' for every §4.2 row). Reads
    // as a directive rather than a slug, because the NCM is who acts on it.
    if (escalation.action) {
      lines.push(`Required action: ${CareCallGuideBaseComponent.ACTION_LABELS[escalation.action] ?? escalation.action}`);
    }
    if (finding.symptomatic) {
      lines.push('Patient is symptomatic — the Low tier symptom gate opened.');
    }
    return lines;
  }

  /**
   * The pre-BRD escalation path: a high-priority `Care Call Escalation Review` task routed to the
   * patient's NCM for EACH tripped field that is not routed as a tiered reason — one task per field
   * since the per-reason split, where it used to contribute one line to the aggregate task.
   *
   * It runs for EVERY guide, including CHF. Making it an either/or on `usesTieredEscalation` was a
   * patient-safety regression: only two CHF fields carry an `escalation` block, so the other 25
   * tripped conditions the guide declares — chest pain, shortness of breath at rest, ED visits,
   * medication side effects, "patient appears in distress" — would have reached nobody at all. They
   * are exactly the call-only findings §0 says the guide must own, and nothing else raises them.
   *
   * TWO behaviour changes, both applied platform-wide:
   *
   * 1. The `patient.provider_id` fallback is gone from `resolveNcmAssignee()`, so no guide can
   *    assign an escalation to the ordering physician, who per BRD-CLIN-2026-006 §10 is "Not a
   *    Welby Care system user" and would never see it (CHF-E1 §3.6).
   * 2. The target is now the patient's first provider in the RN LANE, resolved from the client's
   *    roster, rather than the first provider holding an RN or NP licensure (CHF-E4 §0d).
   *
   * Both mean the task is sometimes NOT created, and the MA is told so — which is the honest
   * outcome, and strictly better than a task that is functionally lost.
   *
   * The roster read is why this is the one branch that has to go async. It is fetched only when
   * there is something to escalate, so an ordinary call still makes no extra request.
   */
  private legacyEscalationReasons(routedIds: Set<string> = new Set()): LegacyEscalationReason[] {
    // Anything already routed as a tiered finding is skipped, so one field never produces TWO tasks
    // — which is what double-handling would now cost, where it used to cost a duplicated line in one
    // body. Today no CHF field is in both sets (the Low bands declare no `escalate_when`), but a
    // future seed that adds one must not raise the same finding twice for the same call.
    return this.escalatingFields()
      .filter(({ section, field }) => !routedIds.has(`${section.id}.${field.id}`))
      .map(({ section, field }) => {
        const note = field.escalate_note ? ` — ${field.escalate_note}` : '';
        const label = this.fieldLabel(section, field);
        // `findingValue` with no measurement IS the captured answer, clipped at the same ceiling
        // the tiered path uses. Reused rather than reformatted so one answer cannot read two ways.
        const value = this.findingValue(section, field, null);
        return {
          key: `${section.id}.${field.id}`,
          label,
          line: `${section.label}: ${label}${note}`,
          block: this.legacyFindingBlock(section, field, label, value),
          value,
        };
      });
  }


  /**
   * Lanes of THIS PATIENT'S PROVIDERS, resolved server-side, for `resolveNcmAssignee`.
   *
   * Reads the NARROW endpoint, not Bulk Transfer's roster — see `ProviderService.getLanesByClient`
   * for why the two are separate, and do not merge them back.
   *
   * The patient's own `providers[]` uids are what is asked about, NOT the client's roster. Asking by
   * client would answer from `client_account_to_user`, and that collection does not contain every
   * provider who holds a panel: measured on dev 2026-09-04, 181 of the 758 patients with a lane-'rn'
   * provider have that provider missing from their client's membership rows, so the client-scoped
   * answer resolved nobody and the escalation reached no one — for 24 % of the population this path
   * exists for. Do not "simplify" the uids back out of this call: it fails silently and it fails on
   * the majority of one client's panel at a time (welby-micro-service #934).
   *
   * FAILS CLOSED, and that is the whole point (CHF-E4 §0d(b)): a patient with no client, a patient
   * with no provider uid at all and a failed read all yield an EMPTY lane map, which resolves no
   * assignee, creates no task and makes the MA read "Escalation needed but no NCM found to assign".
   * Falling back to the licensure rule instead would reproduce the cross-lane bug this replaces on
   * exactly the occasions the read is unavailable — a silent regression to the rule being deleted.
   *
   * The `clientId` stays in the request because it is the tenant boundary the backend checks the
   * CALLER against (`ClientMembershipGuard`), not because it selects the answer.
   */
  private staffLanes$(patient: Patient | null | undefined): Observable<ProviderLaneEntry[]> {
    this.rosterUnavailable = false;
    const clientId = patient?.client_responsible_id;
    const uids = [...new Set((patient?.providers ?? []).map((provider) => provider?.uid).filter(Boolean))] as string[];
    if (!clientId || uids.length === 0) {
      return of([]);
    }
    return this.providerService.getLanesByClient(clientId, uids).pipe(
      timeout(CareCallGuideBaseComponent.ROSTER_TIMEOUT_MS),
      catchError(() => {
        // Fail closed, but do NOT fail permanently: a transient roster failure must leave the MA able
        // to press Generate Note again, or the escalation is lost for the whole capture with no way
        // back short of rebuilding the component. Nothing is reset here any more — no reason key was
        // recorded, and the run's `finalize` clears the in-flight flag on every path.
        // ...and it must not be reported as "this patient has no NCM". That sentence is the genuine
        // cross-lane finding (§0d, 5 patients on dev); an unreachable directory is a different fact
        // and blaming the care team for it sends the MA to the wrong place.
        this.rosterUnavailable = true;
        return of([] as ProviderLaneEntry[]);
      }),
    );
  }

  /**
   * BRD-CLIN-2026-006 §4.1 Low rows — is the patient symptomatic?
   *
   * `gate.fields` are `"sectionId.fieldId"` references and may point OUTSIDE the finding's own
   * section (edema lives in `weight_check`, shortness of breath in `symptom_screen`), which is why
   * this deliberately does NOT reuse `isFieldVisible`'s rule shape: `visible_when` is documented as
   * sibling-scoped and could not express row 1 at all.
   *
   * OR semantics — any one listed field answered affirmatively opens the gate.
   *
   * Two deliberate choices, each with a test:
   * - A HIDDEN gate field never opens the gate (TC-9). A stale 'Yes' sitting behind a `visible_when`
   *   that has since closed must not escalate — the same rule `collectCapturedResponses()` and
   *   `buildPromptResponses()` already apply, so the note and the routing describe the same call.
   * - A missing `sectionId.fieldId` returns false and does NOT throw (TC-15). A typo in the seed
   *   must not break note generation mid-call; `escalationInvariants.js` catches it at seed time,
   *   where it is safe to fail loudly.
   */
  private isSymptomGateOpen(gate: GuideSymptomGate): boolean {
    const expected = gate.equals;
    return (gate.fields ?? []).some((ref) => {
      const [sectionId, fieldId] = ref.split('.');
      const section = this.guide?.sections.find((s) => s.id === sectionId);
      const field = section?.fields.find((f) => f.id === fieldId);
      if (!section || !field || !this.isFieldVisible(section, field)) {
        return false;
      }
      const value = this.controlValue(sectionId, fieldId);
      if (expected !== undefined) {
        return value === expected;
      }
      return value === 'Yes' || value === true; // radio / checkbox default
    });
  }

  /**
   * Every routable finding on the form, resolved from CURRENT form state at submit time.
   *
   * Resolution is never accumulated as the MA types: `auto_check` recomputes on every keystroke and
   * unticks the instant its rules stop matching, so a swelling answer of "Yes" corrected back to
   * "No" must put the finding back to `owner: 'ma'` before the note is generated (§2 point 2).
   *
   * Routing rule: gate OPEN ⇒ `owner: 'rn'`, `symptomatic: true`; gate CLOSED ⇒ `owner` stays as
   * declared, `symptomatic: false`. There is deliberately no "gate evaluated" flag: under §3.2 a
   * held finding writes nothing at all, so such a flag could never reach the audit record anyway.
   *
   * A field is routable only while it is VISIBLE and actually tripped — for the auto-checked band
   * boxes that means ticked, which the mutually-exclusive `auto_check` group already guarantees is
   * the single correct tier for the current reading. NCMs diligence the form themselves, so
   * `suppressEscalations` yields nothing at all (TC-14).
   */
  private collectRoutableFindings(): ResolvedFinding[] {
    if (this.suppressEscalations) {
      return [];
    }
    const findings: ResolvedFinding[] = [];
    this.sortedSections.forEach((section) => {
      section.fields.forEach((field) => {
        const escalation = field.escalation;
        if (!escalation || !this.isFieldVisible(section, field)) {
          return;
        }
        if (!this.escalationFieldTripped(section, field)) {
          return;
        }
        const gate = escalation.symptom_gate;
        const gateOpen = gate ? this.isSymptomGateOpen(gate) : false;
        const measurement = escalation.value_from ? this.measurementValue(section, escalation.value_from) : null;
        findings.push({
          sectionId: section.id,
          fieldId: field.id,
          label: field.label,
          value: this.findingValue(section, field, measurement),
          escalation,
          owner: gateOpen ? 'rn' : escalation.owner,
          symptomatic: gateOpen,
          measurementValue1: measurement ?? undefined,
          units: escalation.units,
        });
      });
    });
    return findings;
  }

  /**
   * Whether a field carrying an `escalation` block is currently tripped.
   *
   * A checkbox (the band boxes) trips while it is ticked — `applyAutoChecks` already resolved the
   * mutually-exclusive group down to the single correct tier for the typed reading, so trusting the
   * tick is what keeps the Low band defined as "≥ 0 and not already Moderate or High".
   *
   * Anything else falls back to the guide's existing `escalate_when` / numeric-bound machinery via
   * `fieldEscalates`, so a future non-checkbox routable field needs no new mechanism here.
   */
  private escalationFieldTripped(section: GuideSection, field: GuideField): boolean {
    if (field.type === 'checkbox') {
      return this.controlValue(section.id, field.id) === true;
    }
    return this.fieldEscalates(section, field);
  }

  /**
   * The number behind a finding, read from the sibling `escalation.value_from` names.
   *
   * A `computed` sibling contributes its DELTA (weight gain); a `number` sibling contributes the
   * reading itself (outreach adherence %). EVERY other type — and an unresolvable or hidden id —
   * yields null, which leaves `measurement_value1` off the alert entirely rather than sending a
   * misleading number. The two allowed types are listed explicitly rather than defaulted into,
   * because a guide document can be edited straight in Firestore without a frontend release (see
   * `computedIsRed`), so the seed validator is not the only thing that can put a `text` field here.
   *
   * Widened from `computed` only in CHF-E4: §4.1 row 3's adherence band IS a percentage, and without
   * this its alert would reach the NCM queue with `getMeasureLabel`'s `0` in the value column beside
   * a real finding. The seed validator's `value_from` check allows exactly these two types.
   */
  private measurementValue(section: GuideSection, fieldId: string): number | null {
    const field = section.fields.find((candidate) => candidate.id === fieldId);
    if (!field || !this.isFieldVisible(section, field)) {
      return null;
    }
    if (field.type === 'computed') {
      return this.computedValue(section, field);
    }
    return field.type === 'number' ? this.numericValue(section.id, field.id) : null;
  }

  /**
   * Human-readable value stored on the alert. Prefers the measurement because that is what the NCM
   * assesses; falls back to the captured ANSWER, formatted exactly as the note prints it, for a
   * finding with no number behind it — the normal case for the radio triggers (a dose change, new
   * edema and a tolerance concern are not readings).
   *
   * A `computed` source is rendered by the SAME `signedDelta` helper the on-screen row uses, so the
   * alert and the value the MA was looking at can never disagree. A `number` source is rendered
   * PLAIN: "+79 %" would misread as a change in adherence rather than adherence itself.
   */
  private findingValue(section: GuideSection, field: GuideField, measurement: number | null): string {
    if (measurement === null) {
      // The ANSWER, not the label. A finding with no number behind it — a radio, a select, a plain
      // checkbox — carries its meaning in what was selected, and `finding_label` already holds the
      // question. Returning the label here made both fields of the alert identical, which reads as
      // "we asked X" with no record of what the answer was, and that lands in an append-only
      // compliance row (`clinical_escalation_events.form_answer.answer`) where it cannot be fixed.
      //
      // This is what chf-e2 did before `findingValue` replaced it: findings carried
      // `value: formatCapturedValue(...)` while `findingMeasurement` supplied the number
      // separately. Only the fields declaring `value_from` kept working after that change, which is
      // why it went unnoticed — they are the ones with a delta to render.
      const answer = this.formatCapturedValue(section, field);
      const max = CareCallGuideBaseComponent.FINDING_VALUE_MAX_LENGTH;
      return answer.length > max ? `${answer.slice(0, max - 1)}…` : answer;
    }
    const source = field.escalation?.value_from
      ? section.fields.find((candidate) => candidate.id === field.escalation?.value_from)
      : undefined;
    const unit = field.escalation?.units ?? source?.unit;
    if (source?.type === 'computed') {
      return CareCallGuideBaseComponent.signedDelta(measurement, unit);
    }
    return unit ? `${measurement} ${unit}` : `${measurement}`;
  }


  /**
   * Resolves the escalation target: the patient's first provider in the RN LANE.
   *
   * The lane is the platform predicate (`roles.isMedicalAssistant` alone) resolved by the backend,
   * NOT the licensure string on `providers[].role`. BT S-03 (#1228) measured that the two disagree
   * on real accounts and that licensure appears in no visibility query; welby-web's own
   * `provider-roster.model.ts` states the rule. And the lane IS the dashboard predicate —
   * `buildAlertRef` sends an `roles.isMedicalAssistant` caller to `owner == 'ma'` and everybody else
   * to `owner == 'rn'` — so assigning across lanes hands the escalation to a queue that filters it
   * out. The work does not fail; it disappears, the same failure mode as the ordering-physician
   * fallback below, reached by another route.
   *
   * Measured on dev 2026-09-03 over 795 patients with a provider: the licensure rule this replaces
   * assigned nobody for 9 patients who do have a lane-'rn' provider (6 of them holding a
   * `Medical Assistant` LICENSURE whose user document has no `roles.isMedicalAssistant`, so their
   * dashboard reads the 'rn' queue), and assigned 5 patients to a provider whose lane is 'ma' —
   * 'Registered Nurse' on paper, MA dashboard in fact.
   *
   * `lanes` are the lanes of THIS PATIENT'S PROVIDERS, and that is load-bearing. An earlier revision
   * of this asked for the lanes of the CLIENT's staff and matched `providers[]` against them, which
   * silently assumed the two populations are the same set. They are not: measured on dev 2026-09-04
   * over the same 795 patients, 196 have a lane-'rn' provider carrying no `client_account_to_user`
   * row on their client, and for 181 of them that is the ONLY lane-'rn' provider they have — so
   * `laneByUid.get(uid)` was `undefined`, the filter dropped them, no assignee resolved, and the MA
   * read "Escalation needed but no NCM found to assign" for 24 % of the patients this path exists
   * for. Indistinguishable, on screen, from a genuine cross-lane finding. The accounts behind it are
   * the panels welby-micro-service #934 already flagged as missing from that collection — Jhonatan
   * Alarcon, Seth Edwards, Laurie Baker, Alejandra Garcia. `staffLanes$` now asks by uid, and the
   * incomplete membership collection is no longer on this path.
   *
   * Ordering is by the provider's existing `rank` (1 = the patient's designated first contact), so
   * that wins rather than array order. Coerced through `Number` with a NaN fallback: `rank` is typed
   * required in the model, but a string `'1'` in Firestore would make the comparator return NaN,
   * which V8 treats as 0 and silently degrades the whole thing back to array order — and this
   * codebase has mixed Firestore types on other fields already.
   *
   * Deliberately NOT re-ranked by licensure inside the lane: "RN es cualquier otro rol" means the
   * lane is the qualifier and nothing else discriminates within it, and BRD §4.1 does not
   * distinguish RN from NP.
   *
   * Guarded on `=== 'rn'`, never on `!== 'ma'`: `lane` is optional because an older backend may omit
   * it, and an unguarded inequality would silently stop filtering.
   *
   * The `patient.provider_id` fallback that used to terminate this chain is deliberately GONE. Per
   * BRD-CLIN-2026-006 §10 the ordering physician is "Not a Welby Care system user", so assigning a
   * `Care Call Escalation Review` task to them silently lost the escalation (CHF-E1 §3.6). Returning
   * `undefined` here makes the caller say so out loud instead. Do not "tidy" either rule back in.
   */
  private resolveNcmAssignee(patient: Patient | null | undefined, lanes: ProviderLaneEntry[]): string | undefined {
    const laneByUid = new Map(lanes.filter((entry) => entry.uid).map((entry) => [entry.uid, entry.lane]));
    return (patient?.providers ?? [])
      .filter((provider) => provider.uid && laneByUid.get(provider.uid) === 'rn')
      .sort((a, b) => CareCallGuideBaseComponent.rankOf(a.rank) - CareCallGuideBaseComponent.rankOf(b.rank))[0]?.uid;
  }


  /**
   * Brings the Clinical Notes panel into view after generating, so the pasted note is
   * visible (the guide form can be long and the user is usually scrolled to the bottom).
   * Deferred so the panel has rendered when it was just opened.
   */
  private scrollToNotePanel(): void {
    setTimeout(() => {
      const notePanel = document.getElementById('note-panel');
      if (notePanel) {
        notePanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 0);
  }

  /**
   * Escapes HTML-special characters so free text is safe inside the rich-text editor.
   * Text-node context only: it escapes `&`, `<` and `>` but NOT quotes, so it must not be
   * used to build an HTML attribute value — every current call site injects into text nodes
   * (`<p>`/`<strong>` content), never attributes.
   */
  private escapeHtmlText(value: string): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Builds the patient-context header prepended to the generated note: patient name,
   * DOB, date of contact (today), the MA/NCM who ran the form, and — when on file —
   * the primary diagnosis and program type. Only non-empty lines are rendered.
   */
  private buildPatientContextHtml(): string {
    const patient = this.patientService.currentPatientService;
    const user = this.authService.user;
    const lines: string[] = [];

    const patientName = `${patient?.firstName ?? ''} ${patient?.lastName ?? ''}`.trim();
    if (patientName) {
      lines.push(`<strong>Patient:</strong> ${this.escapeHtmlText(patientName)}`);
    }
    const dob = this.formatDate(patient?.dob);
    if (dob) {
      lines.push(`<strong>DOB:</strong> ${this.escapeHtmlText(dob)}`);
    }
    lines.push(`<strong>Date of Contact:</strong> ${this.escapeHtmlText(this.formatDate(new Date()))}`);

    const clinicianName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
    if (clinicianName) {
      const role = this.careRole === 'ncm' ? 'NCM' : 'MA';
      lines.push(`<strong>Completed by:</strong> ${this.escapeHtmlText(clinicianName)} (${role})`);
    }
    const mainDiagnosis = resolveMainDiagnosis(this.diagnoses)?.diagnosis;
    if (mainDiagnosis) {
      lines.push(`<strong>Primary Diagnosis:</strong> ${this.escapeHtmlText(mainDiagnosis)}`);
    }
    const programs = patientProgramLabels(this.diagnoses);
    if (programs.length) {
      lines.push(`<strong>Program:</strong> ${this.escapeHtmlText(programs.join(' / '))}`);
    }

    return lines.map((line) => `<p>${line}</p>`).join('');
  }

  /**
   * Formats a Date / ISO string / Firestore Timestamp as M/D/YYYY, or '' when absent/invalid.
   *
   * The format is built from the calendar components instead of `toLocaleDateString()`, which
   * follows the browser locale: on a machine set to a day-first locale the note read
   * "DOB: 21/8/1953" and "Next check-in: 20/8/2026", which a US chart reader parses as a
   * different date. Every date in the note is month/day/year regardless of the workstation.
   */
  private formatDate(value: unknown): string {
    if (!value) {
      return '';
    }
    const raw =
      typeof (value as { toDate?: unknown }).toDate === 'function' ? (value as { toDate: () => Date }).toDate() : value;
    // Date-only strings (e.g. a "YYYY-MM-DD" DOB) parse as UTC midnight via `new Date(string)`,
    // which then renders one day early in negative-offset timezones. Build from the calendar
    // components so the local date is preserved. The regex is end-anchored so only genuine
    // date-only strings take this path — a full ISO datetime ("YYYY-MM-DDTHH:mm:ssZ") falls
    // through to normal Date parsing instead of having its time/zone silently discarded.
    if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split('-').map(Number);
      return this.toMonthDayYear(new Date(y, m - 1, d));
    }
    let date: Date;
    if (raw instanceof Date) {
      date = raw;
    } else if (typeof raw === 'string' || typeof raw === 'number') {
      date = new Date(raw);
    } else {
      return '';
    }
    return isNaN(date.getTime()) ? '' : this.toMonthDayYear(date);
  }

  /**
   * A valid Date as the US clinical date format M/D/YYYY, independent of the browser locale.
   *
   * Read in UTC when the instant is exactly UTC midnight, local otherwise. A date-only value that
   * reached storage as an instant — a DOB written as a Firestore `Timestamp`, or the full ISO
   * string the end-anchored regex in `formatDate` deliberately routes here — is UTC midnight on the
   * day it means, and the local getters would report the day BEFORE it anywhere west of Greenwich:
   * `1942-01-07T00:00:00Z` read in America/New_York is `1942-01-06T19:00:00-05:00`, so the chart
   * note carried the wrong date of birth, a patient-identity field. A value the user actually
   * picked in the browser is built from local components (`new Date(y, m, d)`) and is only UTC
   * midnight when the workstation itself is on UTC, where the two readings agree — so this cannot
   * shift a locally-entered date. A genuine datetime landing exactly on UTC midnight is reported as
   * its UTC calendar day, which is the intent for the date-only fields the guide collects.
   */
  private toMonthDayYear(date: Date): string {
    const isUtcMidnight =
      date.getUTCHours() === 0 &&
      date.getUTCMinutes() === 0 &&
      date.getUTCSeconds() === 0 &&
      date.getUTCMilliseconds() === 0;
    return isUtcMidnight
      ? `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`
      : `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  /**
   * Converts a block of AI prose into simple HTML for the rich-text editor: every line becomes a
   * <p>, with any leading markdown bullet ("- ") or heading marker ("# ") stripped so it never
   * shows as literal text. Nothing here becomes an <h3>: the only headings the note carries are the
   * guide section titles emitted by `buildNoteSectionsHtml`, so a heading the model invented is
   * downgraded to prose rather than passed off as a section of the form.
   */
  private formatNoteAsHtml(note: string): string {
    return (note || '')
      .split('\n')
      .map((line) => this.stripLineMarkers(line.trim()))
      .filter((line) => line.length > 0)
      .map((line) => `<p>${this.escapeHtmlText(line)}</p>`)
      .join('');
  }

  /**
   * The whole note body: one section per GUIDE section, titled with the guide's own label and in
   * the guide's own order, so the note reads like the form the MA just filled in (T2DM shows
   * "Blood Sugar Review", HTN "Blood Pressure Review", COPD "Breathing & Symptom Review"). The
   * model supplies the prose; this decides the structure, which is why a section can never come
   * back missing or reported as blank while the guide holds answers for it.
   *
   * Each section carries the model's prose followed by the responses exactly as captured. That
   * pairing is what replaced the old "Guide Responses (as captured)" appendix: nothing typed can be
   * lost, and it is now filed under the section it was answered in instead of in a block at the end.
   *
   * A section carrying a `not_addressed` marker in `responses` is titled and marked as not
   * addressed — it documents that the topic was asked. Prose the model wrote under no recognizable
   * header is kept under a heading of its own rather than dropped or disguised as a form section.
   */
  private buildNoteSectionsHtml(
    note: string,
    captured: GuideCapturedResponse[],
    responses: GuideFormResponse,
    sections: Pick<GuideSection, 'id' | 'label'>[],
  ): string {
    const { bodies, unassigned } = this.parseNoteSections(note, sections);
    const entriesBySection = this.groupCapturedBySection(captured);
    const parts: string[] = [];
    sections.forEach(({ id, label }) => {
      const narrative = (bodies.get(id) ?? []).filter((line) => !this.isNotAddressedLine(line));
      const entries = entriesBySection.get(id) ?? [];
      const presentedAndBlank = responses[id]?.[CareCallGuideBaseComponent.NOT_ADDRESSED_KEY] === true;
      if (!narrative.length && !entries.length && !presentedAndBlank) {
        return;
      }
      parts.push(`<h3>${this.escapeHtmlText(label)}</h3>`);
      if (narrative.length) {
        parts.push(this.formatNoteAsHtml(narrative.join('\n')));
      }
      if (entries.length) {
        parts.push(entries.map((entry) => this.capturedEntryHtml(entry)).join(''));
      }
      if (!narrative.length && !entries.length) {
        parts.push(`<p>${NOTE_NOT_ADDRESSED_BODY}</p>`);
      }
    });
    // Same filter as the per-section narrative, for the same reason: a "Not addressed this visit"
    // body that arrived under a header this frontend cannot match is still the claim "this topic
    // was asked about and not covered" — and here it would be printed under a heading that is not
    // even a topic of the form. `buildCapturedOnlyHtml` refuses to make that claim on the error
    // path; it must not be made as a side effect of an unrecognized header on the success path.
    const loose = unassigned.filter((line) => !this.isNotAddressedLine(line));
    if (loose.length) {
      parts.push(`<h3>${NOTE_UNSTRUCTURED_HEADING}</h3>`, this.formatNoteAsHtml(loose.join('\n')));
    }
    return parts.join('');
  }

  /**
   * True when a note line is the "topic asked, not covered" placeholder.
   *
   * Compared through `normalizeNoteHeader` rather than by equality: the prompt tells the model to
   * write the placeholder exactly, but that is an instruction, not a guarantee. A trailing period
   * or different casing slipping past an exact match would print "Not addressed this visit."
   * directly above the readings captured for that very section — a contradiction a chart reviewer
   * cannot resolve.
   */
  private isNotAddressedLine(line: string): boolean {
    return normalizeNoteHeader(this.stripLineMarkers(line)) === normalizeNoteHeader(NOTE_NOT_ADDRESSED_BODY);
  }

  /**
   * The captured responses rendered on their own, for when there is no narrative to pair them with
   * (the AI call failed or came back empty). Empty when nothing was captured at all: a chart note
   * whose every section reads "Not addressed this visit" carries no information, and the snackbar
   * must not report it as responses added to the chart.
   *
   * The `not_addressed` markers are deliberately withheld, so this renders ONLY what was answered.
   * An error path must not put a dozen "the topic was asked and not covered" assertions into a note
   * that supports billing — that is a clinical claim, and here it would be a side effect of a
   * failed HTTP call.
   */
  private buildCapturedOnlyHtml(captured: GuideCapturedResponse[], sections: Pick<GuideSection, 'id' | 'label'>[]): string {
    return captured.length ? this.buildNoteSectionsHtml('', captured, {}, sections) : '';
  }

  /**
   * The sections the note may contain, in on-screen order. A guide section is included once it can
   * hold something — it reached the payload, or it produced a captured response (a `computed`-only
   * section has no control yet still shows a value). A pure script / talking-points section is left
   * out: it can never render anything, and including it would let it claim a header that belongs to
   * a section the MA actually answered. Sections that exist only in the captured responses (the
   * synthetic NCM escalation) are appended last, so their free text still gets a home. Ids are
   * deduped, so a guide authored with a repeated section id cannot print the same rows twice.
   */
  private noteSectionOutline(
    captured: GuideCapturedResponse[],
    responses: GuideFormResponse,
  ): Pick<GuideSection, 'id' | 'label'>[] {
    const outline: Pick<GuideSection, 'id' | 'label'>[] = [];
    const known = new Set<string>();
    const add = (id: string, label: string): void => {
      if (known.has(id)) {
        return;
      }
      known.add(id);
      outline.push({ id, label: String(label ?? '').trim() || this.humanizeFieldId(id) });
    };
    const answered = new Set(captured.map((entry) => entry.sectionId));
    this.sortedSections.forEach((section) => {
      if (responses[section.id] || answered.has(section.id)) {
        add(section.id, section.label);
      }
    });
    captured.forEach((entry) => add(entry.sectionId, entry.sectionLabel));
    return outline;
  }

  /**
   * Splits the model's note into the body lines of each guide section, plus everything that sat
   * under no recognized header. A header the model repeats merges into the one body instead of
   * yielding a duplicate heading.
   *
   * An ATX heading ("### Label") that matches no guide section ends the current section rather than
   * being appended to it. Continuing to append there filed the unmatched section's title AND its
   * whole body under the previous guide section — with the currently deployed backend prompt
   * (13 canonical headers, of which "Contact Type", "Care Gaps Addressed", "Escalation/Follow-up"
   * and "Next Contact" match no section in any seeded guide) that put care-gap interventions under
   * "RPM Device Monitoring" and the `[ESCALATION FLAG]` lines under "Safety Review & Close". Such
   * prose belongs under `NOTE_UNSTRUCTURED_HEADING`, which is what the reset routes it to.
   *
   * Only a real ATX heading terminates a section, even though the looser shapes
   * `markedHeaderText` accepts can still MATCH one. Those shapes have false positives — the model
   * bolds an ordinary sentence, or writes "#1 concern is post-meal spikes" — and a false positive
   * that terminates takes the REST of the section with it: the section would render "Not addressed
   * this visit" while its prose sat at the bottom of the note, which is the defect this whole path
   * exists to prevent. A false positive that matches nothing is simply body text.
   *
   * An unmatched heading is held back until a body line actually follows it. Against the deployed
   * prompt four canonical headers map to no section, so emitting the title unconditionally ended a
   * typical note with three topic names and no information under a heading promising narrative.
   */
  private parseNoteSections(
    note: string,
    sections: Pick<GuideSection, 'id' | 'label'>[],
  ): { bodies: Map<string, string[]>; unassigned: string[] } {
    const headers = this.noteHeaderIndex(sections);
    const bodies = new Map<string, string[]>();
    const unassigned: string[] = [];
    let current = unassigned;
    // Title of an unmatched heading, printed only once something is written under it. The
    // placeholder does not count as content: it is filtered out of the loose block downstream, and
    // flushing the title for it would leave exactly the bare, content-free title line.
    let pendingLooseTitle = '';
    const push = (line: string): void => {
      if (current === unassigned && pendingLooseTitle && !this.isNotAddressedLine(line)) {
        unassigned.push(pendingLooseTitle);
        pendingLooseTitle = '';
      }
      current.push(line);
    };
    (note || '')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .forEach((line) => {
        const marked = this.markedHeaderText(line);
        if (!marked) {
          push(line);
          return;
        }
        const sectionId = headers.get(normalizeNoteHeader(marked)) ?? '';
        if (sectionId) {
          current = bodies.get(sectionId) ?? [];
          bodies.set(sectionId, current);
          pendingLooseTitle = ''; // it had no body of its own, so there is no title to print
          return;
        }
        if (!/^#{1,6}\s+\S/.test(line)) {
          // Marked, but not an ATX heading, and it matches nothing: body text. A bolded line keeps
          // only its inner text so no literal "**" reaches the chart; anything else — "#1 concern
          // is post-meal spikes" — keeps its own characters, which the prompt tells the model to
          // reproduce faithfully.
          push(this.fullyBoldText(line) || line);
          return;
        }
        // Unrecognized heading: it is still a heading, so it belongs to nothing that precedes it.
        // The cleaned text is what gets kept — printing the raw line would put "### Care Gaps
        // Addressed", markers and all, into a clinical chart note.
        current = unassigned;
        pendingLooseTitle = marked;
      });
    return { bodies, unassigned };
  }

  /**
   * The heading text of a line that is MARKED as a header, or '' when the line is body text.
   *
   * A line only counts as a header when it is marked as one: "### Label", which is what both the
   * current and the previous prompt ask for, or a fully bolded line, which is what the model falls
   * back to when it drifts. Matching bare text was how the MA's own free text could pass for a
   * header — the model reproduces that text faithfully, so a summary containing a line like
   * "Symptom Screen" had it swallowed (a matched line is consumed, never printed) and every line
   * after it refiled under a section it was never written in.
   *
   * The accepted shapes are deliberately loose WITHIN "marked": a missing space ("###Label"),
   * closing hashes ("### Label ###"), bold inside the heading ("### **Label**") and a trailing
   * colon on a bolded line ("**Label**:") are all stylistic drift the model produces for the same
   * section, and rejecting one of them costs the whole section body, not just the heading line.
   * `normalizeNoteHeader` strips the residual emphasis before the lookup.
   *
   * Matching a header here is not the same as ENDING a section — see `parseNoteSections`, which
   * requires a real ATX heading for that, because these shapes have false positives.
   */
  private markedHeaderText(line: string): string {
    if (/^#{1,6}\s*\S/.test(line)) {
      // Clipped for the same reason `normalizeNoteHeader` clips before its own trailing-punctuation
      // regex: `\s*#+$` is unanchored at the start, so a long run of hashes retries from every
      // position and backtracks quadratically. Only this branch needs it — the fully-bold regex has
      // no ambiguous overlap, and clipping it would stop a long bolded label from matching at all.
      return line
        .slice(0, MAX_HEADER_LENGTH)
        .replace(/^#{1,6}\s*/, '')
        .replace(/\s*#+$/, '')
        .trim();
    }
    return this.fullyBoldText(line);
  }

  /** The inner text of a line that is entirely bold ("**Blood Sugar Review**", "**Label**:"), or ''. */
  private fullyBoldText(line: string): string {
    return /^\*\*([^*]+)\*\*\s*:?$/.exec(line)?.[1].trim() ?? '';
  }

  /**
   * Normalized header text -> guide section id. Each section is registered under its own label, its
   * raw id (the header the prompt asks for when a section has no label) and the header the previous
   * prompt used for it, so this frontend reads a note from either the current or the old
   * welby-micro-service prompt (they deploy separately). A key already claimed by an earlier
   * section is not overwritten: the first section in form order wins.
   */
  private noteHeaderIndex(sections: Pick<GuideSection, 'id' | 'label'>[]): Map<string, string> {
    const headers = new Map<string, string>();
    const register = (header: string, sectionId: string): void => {
      const key = normalizeNoteHeader(header);
      if (key && !headers.has(key)) {
        headers.set(key, sectionId);
      }
    };
    sections.forEach(({ id, label }) => register(label, id));
    sections.forEach(({ id }) => register(id, id));
    sections.forEach(({ id, label }) => register(legacyNoteSection(id, label), id));
    return headers;
  }

  /** Captured responses bucketed by the guide section they were answered in. */
  private groupCapturedBySection(captured: GuideCapturedResponse[]): Map<string, GuideCapturedResponse[]> {
    const grouped = new Map<string, GuideCapturedResponse[]>();
    captured.forEach((entry) => {
      const bucket = grouped.get(entry.sectionId) ?? [];
      bucket.push(entry);
      grouped.set(entry.sectionId, bucket);
    });
    return grouped;
  }

  /** A note line without its markdown heading / bullet prefix. */
  private stripLineMarkers(line: string): string {
    return line
      .replace(/^#{1,6}\s+/, '')
      .replace(/^[-*]\s+/, '')
      .trim();
  }

  /**
   * Flat map of "sectionId" and "sectionId.fieldId" -> human label, for the note. Blank
   * labels are synthesized (see `fieldLabel`) and the synthetic NCM escalation section is
   * declared here so the generator can name it.
   */
  private buildFieldLabels(): Record<string, string> {
    const labels: Record<string, string> = {};
    this.sortedSections.forEach((section) => {
      labels[section.id] = section.label;
      section.fields.forEach((field) => {
        labels[`${section.id}.${field.id}`] = this.fieldLabel(section, field);
      });
    });
    return labels;
  }

  /** Every currently-tripped (section, field) pair. Single source of truth for the escalation scan. */
  private escalatingFields(): { section: GuideSection; field: GuideField }[] {
    const result: { section: GuideSection; field: GuideField }[] = [];
    this.guide?.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.type !== 'info' && field.type !== 'computed' && this.fieldEscalates(section, field)) {
          result.push({ section, field });
        }
      });
    });
    return result;
  }

  /** Human-readable escalation triggers detected on the completed form. Uses the same synthesized
   *  labels as the note payload, so a blank-labelled field never yields a nameless trigger like
   *  "Symptoms: " in the prompt or in the NCM's task description.
   *
   *  Each string is the finding and the recommendation, and NOTHING ELSE — it does not name the
   *  protocol clause behind the trigger; see below. Composition only: `GenerateNotePayload.escalations`
   *  stays `string[]`, which is what keeps the payload shape-compatible with the backend DTO and this
   *  change free of any deploy ordering (§3.4).
   *
   *  ## NO CITATION HERE EITHER — THE RULE REACHES THIS LINE TOO
   *
   *  This line has moved three times, so the reasoning belongs beside the code rather than scattered
   *  across merged commit messages.
   *
   *  It used to end `— BRD-CLIN-2026-006 §4.1 Moderate`. It was removed, restored on the argument
   *  that the generated note is the §5.1 billing record and a PAYER is a reader the
   *  no-section-numbers rule does not cover, and is now removed again. Three things settled it:
   *
   *  1. `escalations` is an INPUT to note generation, not the note. The payload is POSTed and a
   *     model-written note comes back, so a citation interpolated into this prose is a suggestion to
   *     a model, never a guarantee in the artifact a payer receives. It cannot carry a compliance
   *     obligation.
   *  2. §5.1 traceability never depended on it. `protocol_clause` and `protocol_clause_text` ride on
   *     the task document, which `recordEscalationEventFromTask` copies into
   *     `clinical_escalation_events` — a queryable field, not prose.
   *  3. It is what a clinician reads. The document id and section number name the requirement the way
   *     this codebase names it, which is not something a nurse can act on.
   *
   *  If the billing record is ever found to genuinely require a protocol reference, it belongs in a
   *  STRUCTURED field on the payload, where a model cannot reword it away — not back in this string.
   */
  private collectEscalations(): string[] {
    return this.escalatingFields().map(({ section, field }) => {
      const note = field.escalate_note ? ` — ${field.escalate_note}` : '';
      // The line ends at the recommendation. See the docblock for why no citation follows it.
      //
      // Removing it also removes a live defect rather than fixing one: the merged version read
      // `clause.tierLabel` directly, and a §4.2 entry carries an `urgencyLabel` while a §4.3 entry
      // carries a `triggerLabel` — so every §4.2 escalation was emitting the literal word
      // "undefined" into the note.
      return `${section.label}: ${this.fieldLabel(section, field)}${note}`;
    });
  }

  /**
   * Wires the data-driven auto-check rules: checkbox fields declaring `auto_check`
   * self-tick when the sibling vitals typed in their section hit the configured
   * thresholds, reproducing the source form's live JS. Runs for every role — the
   * boxes are documentation of the typed reading. The escalation consequences stay
   * role-gated elsewhere (fieldEscalates / the MA-only NCM task), so an NCM sees the
   * bands fill but no escalation flag and no task.
   */
  private setupAutoChecks(doc: CareCallGuideDoc): void {
    doc.sections.forEach((section) => {
      const autoFields = section.fields.filter((f) => f.type === 'checkbox' && f.auto_check?.rules?.length);
      if (!autoFields.length) {
        return;
      }
      this.guideForm
        .get(section.id)
        ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => this.applyAutoChecks(section, autoFields));
    });
  }

  /**
   * Reconciles the auto-check boxes with the currently typed vitals. Fields sharing a
   * `group` form a severity chain — only the highest-severity match (lowest priority
   * number) is ticked and the rest are cleared (else-if semantics, e.g. SBP 185 ticks
   * the crisis box, not the elevated one). The reconciliation is fully reactive: a box
   * ticked for a prior value is unticked once that value no longer matches.
   *
   * ## The winner's SIBLING WINDOWS tick with it, when they are the same protocol row
   *
   * The chain decides the RANK. It used to decide the single box as well, and for a chain of
   * genuinely distinct severities — every RPM band on the platform — that is the same thing. It is
   * not the same thing for `chf_ma`'s `weight_gain`, where the six bands are three §4.1 rows read
   * through TWO WINDOWS each:
   *
   * | §4.1 row | 24-hour leg | 3-day leg |
   * |---|---|---|
   * | High | `>= 3 lbs since yesterday` | `>= 5 lbs over 3 days` |
   * | Moderate | `>= 2 lbs since yesterday` | `>= 3 lbs over 3 days` |
   * | Low | `< 2 lbs since yesterday` | `< 3 lbs over 3 days` |
   *
   * A patient up 2 lbs since yesterday AND 3 lbs over three days crosses the Moderate row through
   * both windows. The chain ticked the 24-hour one and cleared the 3-day one, so the NCM's queue
   * named one window and the other was not recorded anywhere (Andres Rodriguez, welby-dev
   * 2026-09-11, on `frvVMhSi1ITewq59jakqMz4HWP02`). Both windows now tick and each writes its own
   * task.
   *
   * SAME `escalation.clause` AS THE WINNER is the test: a clause id names a row of the protocol, so
   * two bands citing one clause are two WINDOWS onto one row. Audited against every guide on
   * welby-dev 2026-09-11 — the only grouped bands anywhere that share a clause are those three CHF
   * weight pairs. Every RPM chain (`spo2`, `rpm_bp`, `bg`, `bp_today_flow`, …) carries no
   * `escalation` at all, so its winner has no clause and the guard is false for it: SBP 185 still
   * ticks the crisis box alone.
   *
   * NOT `escalation.tier`, which would be the same answer today by accident. Two bands can share a
   * tier and grade different findings — a Moderate weight band and a Moderate adherence band would
   * co-tick on a tier test and must not. The clause is what says "one row".
   *
   * ## A SHARED CLAUSE MEANS ONE ROW, NOT ONE TIER
   *
   * That distinction was not in the first draft of this comment, which said two bands citing one
   * clause reach one row "by construction" and left the reader to infer they must therefore grade
   * alike. They need not, and as of welby-firestore's 24-hour regrade they do not (juana98, PR
   * review, 2026-09-11): `weight_gain_moderate_24h` is `tier: 'high'` and `weight_gain_moderate_3d`
   * is `tier: 'moderate'`, and BOTH cite `#4.1-moderate-weight`.
   *
   * So a patient up 2.5 lbs in 24 hours and 3.5 lbs over three days gets two tasks off one clause,
   * one `Tier: High` at priority `high` and one `Tier: Moderate` at priority `medium`, carrying
   * identical `Protocol text`. That is the intended outcome of grading the windows independently
   * (Andres Rodriguez, 2026-09-11) and it is what this method must keep doing: the alternative is
   * dropping the slower window's finding, which is the defect the co-tick was added to fix.
   *
   * What it means for anyone changing this: the clause answers "is this the same protocol row", and
   * nothing more. Do not derive a tier, a priority or a body from the fact that two bands share one,
   * and do not "fix" the two tasks into one by comparing tiers — they disagree on purpose.
   *
   * The RANK still resolves first, so a Moderate winner never drags a Low sibling along: the Low
   * bands cite `#4.1-low-weight` and the winner cites `#4.1-moderate-weight`.
   *
   * ## WHICH BOXES MAY DECLARE `auto_check` AT ALL — a rule, not a preference
   *
   * A box reconciled here is NOT RETRACTABLE. There is no manual-override flag: every member of
   * every group is written from the numbers on every keystroke, so an MA who unticks a box emits on
   * the section, re-enters this method, and watches it come straight back. Pinned by
   * "an auto-ticked box is not retractable".
   *
   * So a box may declare `auto_check` only when the READING ALONE makes its label true:
   *
   * | label | may auto-tick | why |
   * |---|---|---|
   * | `SBP ≥160 mmHg` (`chf_ma`) | yes | the number is the whole claim |
   * | `SpO₂ ≤88% OR >95%` (`copd_ma`) | yes | an OR whose EVERY leg is numeric, so the reading settles the label either way |
   * | `SpO₂ <85% OR cyanosis OR confusion` (`copd_ma`) | **no** | an OR only one of whose legs is numeric |
   * | `HR >110 bpm WITH SYMPTOMS` (`chf_ma`) | **no** | an AND whose second half no field in the section holds |
   *
   * The AND row is the dangerous one, because every one of these boxes carries `escalate_when`.
   * Auto-ticking it would assert symptoms nobody confirmed, raise an escalation on that assertion,
   * and leave the MA unable to withdraw it. Omitting `auto_check` is what keeps the box hers —
   * `setupAutoChecks` subscribes only fields declaring rules, so an unwired box is never written to.
   * Pinned by "a box the seed left unwired stays under the MA control".
   *
   * ### "OR" IS NOT THE TEST — EVERY LEG NUMERIC IS. A partial OR fails the OTHER WAY: it CLEARS
   *
   * The AND row asserts something nobody confirmed. A partial-numeric OR erases something somebody
   * DID, which is quieter and therefore worse. `copd_ma.spo2_critical` reads "SpO₂ <85% OR cyanosis
   * OR confusion" and rules only `{ spo2_today, below: 85 }`. At SpO₂ 90 with cyanosis observed on
   * the call the MA ticks the box, her click emits on the section, and this method sets it back to
   * false with nothing said — the numeric leg does not match, so the box is not in `ticked`, so it
   * is written false. `rpm_spo2_critical` is the same shape in the same guide. Pinned by
   * "a partial-numeric OR clears the MA own tick".
   *
   * Note what does the erasing: DECLARING RULES, not losing the rank. A solo box whose rule stops
   * matching is cleared by the same line — `matched` empty gives `ticked` empty and every member of
   * the group is written false. Losing to a sibling is only the commonest way to reach it. So the
   * cost of a rule that cannot express the whole label is paid on every keystroke, chain or no
   * chain, and the box the seed leaves alone is the only one that stays the MA's.
   *
   * So `spo2_high` ("≤88% OR >95%") is safe because EITHER leg is settled by the reading, while
   * `spo2_critical` and `rpm_spo2_critical` are not. Those two are live defects in seeded clinical
   * copy, named here so the "yes" row above is not read as blessing them. The fix is to drop their
   * `auto_check` — a `copd_ma` seed change, not a frontend one — and no ticket carries it yet.
   *
   * ### THE RULE IS ALREADY CONTRADICTED IN THE GUIDE THE "YES" ROW CITES
   *
   * `copd_ma` ships the AND shape wired today: `rpm_hr_high` reads "HR >120 bpm with respiratory
   * symptoms" and declares `{ rpm_hr, above: 120 }`, and `rpm_spo2_moderate` reads "SpO₂ 89–91% (at
   * or below baseline, symptomatic, or not improving)" and declares `{ rpm_spo2, at_or_below: 91 }`.
   * HR 121 with no symptoms ticks, asserts, escalates and cannot be unticked — exactly the mechanism
   * pinned by "an auto-ticked box is not retractable", on a different seed. Marcela's scenario is
   * therefore already live on COPD. Known, unfixed, no ticket yet, and out of this change's scope
   * for the same reason as above: it is seeded copy. Named so a reader checking the rule against
   * `copd_ma` finds it contradicted there KNOWINGLY rather than concluding the rule is dead.
   *
   * ## WHY SIX BOXES IN `chf_ma.rpm_device_monitoring` STAY UNWIRED — for TWO different reasons
   *
   * `applyRpmFlowAutoChecks.js` (welby-firestore) wires `flow_sbp_over_160` and leaves the other SIX
   * alone. THREE are the AND row above — `flow_sbp_under_90_symptomatic`,
   * `flow_sbp_180_or_dbp_120_symptomatic`, `flow_hr_over_110_symptomatic`. An MA reading those
   * unticked at HR 112 is the rule working, not a gap: Marcela Malaver raised exactly that on
   * 2026-09-11 and the answer is the label, which only Clinical can change.
   *
   * The OTHER THREE pass the rule and must still not be wired, which is why the rule alone is not
   * enough to act on. `flow_weight_up_2lbs` reads "Weight up ≥2 lbs since yesterday" and the section
   * holds `device_reading_vs_yesterday` as a `number`: the reading alone settles that label, so a
   * mechanical application of the rule WIRES IT. Two things stop it:
   *
   * - DUPLICATE ESCALATION. `weight_check.weight_gain_*` already auto-checks the SAME §4.1
   *   thresholds off `weight_today − weight_yesterday`, and every band there escalates. Wiring the
   *   RPM copies raises one §4.1 row twice on one call — the hazard class of the `vital_flag_CHF`
   *   divergence recorded below, except that one is a deliberate 1-vs-2 and this would be an
   *   accidental doubling nobody decided.
   * - AN AMBIGUOUS SOURCE. Nothing in the document says whether the MA types a DELTA (+2) or
   *   YESTERDAY'S WEIGHT (198) into `device_reading_vs_yesterday`, and a 198 read as a delta is
   *   `at_or_above: 2` for every patient alive. `flow_weight_up_3lbs_or_5lbs_3days` additionally has
   *   no 3-day field in this section — the partial OR again — and `flow_weight_up_7lbs_72hrs` has no
   *   72-hour window to compute from at all. `rpmFlowAutoChecks.js` carries the long form.
   *
   * So the rule is NECESSARY AND NOT SUFFICIENT: passing it makes a box eligible, not correct.
   *
   * ## Known divergence from the RPM engine, accepted deliberately
   *
   * `vital_flag_CHF` grades the SAME §4.1 Moderate row and raises ONE escalation for it: `weight_1`
   * (24 h) and `weight_moderate_3d` (3 days) sit at priority 1 and 2 inside tier 2, and the
   * evaluation loop returns after the first rule that fires — its own seed records the reasoning as
   * "both cite the same clause and route identically, so the tiebreak changes nothing clinically".
   * So a reading that reaches the NCM through the device raises one task and the same reading
   * captured on a care call now raises two. That is a product decision (Andres Rodriguez,
   * 2026-09-11), taken with the divergence named rather than discovered later.
   */
  private applyAutoChecks(section: GuideSection, autoFields: GuideField[]): void {
    const groups = new Map<string, GuideField[]>();
    autoFields.forEach((f) => {
      const group = f.auto_check?.group ?? `__solo__${f.id}`;
      groups.set(group, [...(groups.get(group) ?? []), f]);
    });
    groups.forEach((fields) => {
      // Every checkbox whose rules match the currently typed vitals, most severe first.
      const matched = fields
        .slice()
        .sort((a, b) => (a.auto_check?.priority ?? 0) - (b.auto_check?.priority ?? 0))
        .filter((f) => (f.auto_check?.rules ?? []).some((rule) => this.autoCheckRuleMatches(section, rule)));
      // The rank, then the winner's own row. An empty `matched` leaves `ticked` empty, which is what
      // clears a box ticked for a value that no longer matches.
      const winner = matched[0];
      const ticked = winner ? matched.filter((f) => f === winner || this.sameProtocolRow(winner, f)) : [];

      fields.forEach((f) => this.setCheckbox(section.id, f.id, ticked.includes(f)));
    });
  }

  /**
   * Whether two bands of one severity chain are the SAME protocol row, reached through different
   * windows — see `applyAutoChecks`.
   *
   * ONE ROW, not one tier. The two bands may be graded differently and currently are: since
   * welby-firestore's 24-hour regrade, `weight_gain_moderate_24h` is High and
   * `weight_gain_moderate_3d` is Moderate, both on `#4.1-moderate-weight`. This predicate answers
   * only "same row"; the tier is read off each band separately, where it belongs.
   *
   * FAILS CLOSED on a winner with no clause. A guide can be edited straight in Firestore, and every
   * RPM band on the platform carries no `escalation` block: without the truthiness check,
   * `undefined === undefined` would make every member of every RPM chain the "same row" as its
   * winner, and SBP 185 would tick the crisis box AND the elevated one AND the moderate one. That is
   * the failure this guard exists to prevent, so it is written as a guard rather than as an equality.
   */
  private sameProtocolRow(winner: GuideField, candidate: GuideField): boolean {
    const clause = winner.escalation?.clause;
    return !!clause && candidate.escalation?.clause === clause;
  }

  /** Sets a checkbox control without re-triggering the section's valueChanges (avoids recursion). */
  private setCheckbox(sectionId: string, fieldId: string, value: boolean): void {
    const control = this.guideForm.get(`${sectionId}.${fieldId}`);
    if (control && control.value !== value) {
      control.setValue(value, { emitEvent: false });
    }
  }

  private autoCheckRuleMatches(section: GuideSection, rule: GuideAutoCheckRule): boolean {
    const base = this.componentValue(section.id, rule.field, rule.component);
    if (base === null) {
      return false;
    }
    let value = base;
    if (rule.minus_field) {
      const subtrahend = this.componentValue(section.id, rule.minus_field, rule.component);
      if (subtrahend === null) {
        return false;
      }
      value = base - subtrahend;
    }
    return this.numericBoundHit(value, rule);
  }

  /** True when `value` trips any of the four numeric bounds. Shared by escalation checks and auto-check rules. */
  private numericBoundHit(
    value: number,
    bounds: { above?: number; below?: number; at_or_above?: number; at_or_below?: number },
  ): boolean {
    if (bounds.above !== undefined && value > bounds.above) {
      return true;
    }
    if (bounds.below !== undefined && value < bounds.below) {
      return true;
    }
    if (bounds.at_or_above !== undefined && value >= bounds.at_or_above) {
      return true;
    }
    if (bounds.at_or_below !== undefined && value <= bounds.at_or_below) {
      return true;
    }
    return false;
  }

  /** Numeric value of a control, or null when empty / not a number. */
  private numericValue(sectionId: string, fieldId: string): number | null {
    const value = this.controlValue(sectionId, fieldId);
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const numeric = typeof value === 'number' ? value : parseFloat(value as string);
    return isNaN(numeric) ? null : numeric;
  }

  /**
   * Numeric value of one component of a control. Default (or 'systolic') reads the
   * leading number — same as numericValue, so "120/80" and plain "120" both give 120.
   * 'diastolic' reads the number after the first '/' (e.g. 80 from "120/80"), used to
   * grade a combined Blood Pressure field off its diastolic side. Null when absent/NaN.
   */
  private componentValue(sectionId: string, fieldId: string, component?: 'systolic' | 'diastolic'): number | null {
    if (component !== 'diastolic') {
      return this.numericValue(sectionId, fieldId);
    }
    const value = this.controlValue(sectionId, fieldId);
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const parts = String(value).split('/');
    if (parts.length < 2) {
      return null;
    }
    const numeric = parseFloat(parts[1]);
    return isNaN(numeric) ? null : numeric;
  }

  /**
   * Restores an in-progress form cached for this patient + guide (saved while the user
   * navigated to another patient-detail tab, e.g. RPM Data). Date fields are revived
   * from their JSON string representation so the datepickers keep working. The patch
   * re-triggers the auto-check reconciliation, so auto-ticked boxes stay consistent.
   */
  private restoreCachedForm(doc: CareCallGuideDoc): void {
    const patientId = this.cachePatientId;
    if (!patientId) {
      return;
    }
    const cached = this.formCacheService.load(patientId, this.resolvedDiagnosis);
    if (!cached) {
      return;
    }
    // Restore across revisions ON PURPOSE. Angular's `patchValue` ignores keys with no matching
    // control, so a field the new revision removed or renamed is dropped and everything still
    // present is kept. That is strictly better than the version-equality guard this replaces, which
    // turned a partial mismatch into total loss of an in-progress call (§0d) — including on a plain
    // page refresh, where `sessionStorage` had the answers all along and the guard discarded them.
    this.guideForm.patchValue(this.reviveDates(doc, cached.responses ?? {}));
    // SILENT on a revision mismatch too — the same call as the re-emission guard above, for the same
    // reason. A seed or a migration bumps `version` on the one global document, so after any such run
    // EVERY cached form in the platform mismatches and every MA gets a toast on open, about a change
    // that in practice removed nothing they had answered (`patchValue` already dropped whatever no
    // longer exists, which is the actual protection). The warning fired on the common case and stayed
    // quiet on the rare one, so it trained people to dismiss it.
    //
    // `version` STAYS on the cache entry. It is no longer read here, and that is not a reason to drop
    // it: it is the only record of which revision an answer set was captured against, it is what a
    // future "these 3 answers were dropped" notice would diff on, and removing it from the stored
    // shape would need a cache migration to add back. Keep writing it.
  }

  /**
   * Persists the form to the session cache as the user types, so leaving the guide and coming
   * back restores the progress. Debounced to one storage write per typing pause instead of one
   * per keystroke.
   */
  private setupFormPersistence(): void {
    if (!this.cachePatientId) {
      return;
    }
    this.guideForm.valueChanges
      .pipe(debounceTime(CareCallGuideBaseComponent.FORM_CACHE_DEBOUNCE_MS), takeUntil(this.unsubscribe$))
      .subscribe(() => this.persistFormToCache());
  }

  /**
   * Writes the current form state to the session cache. No-op without a patient/form or once
   * finalized. When the form is empty it clears any stored entry instead of
   * writing one: an untouched guide should never leave a PHI-shaped entry at rest, and clearing
   * every field is honored on return rather than restoring stale data.
   */
  private persistFormToCache(): void {
    const patientId = this.cachePatientId;
    if (!patientId || !this.guideForm || this.formCacheFinalized) {
      return;
    }
    // Defensive: never write this form if the active patient is no longer the one this
    // instance loaded for (guards against a reused component instance across a patient switch).
    if (this.patientService.currentPatientServiceID !== patientId) {
      return;
    }
    if (!this.hasFormData) {
      this.formCacheService.clear(patientId, this.resolvedDiagnosis);
      return;
    }
    this.formCacheService.save(patientId, this.resolvedDiagnosis, {
      version: this.guide?.version ?? '',
      responses: this.guideForm.value,
    });
  }

  /** Drops the cached state and stops any further persistence (in-flight debounce, destroy flush). */
  private clearFormCache(): void {
    const patientId = this.cachePatientId;
    if (!patientId) {
      return;
    }
    this.formCacheService.clear(patientId, this.resolvedDiagnosis);
    this.formCacheFinalized = true;
  }

  /**
   * JSON round-trips turn Date values into ISO strings; convert them back to Date for
   * the guide's date fields so Material datepickers receive real Date objects.
   */
  private reviveDates(doc: CareCallGuideDoc, responses: GuideFormResponse): Record<string, Record<string, GuidePrimitive | Date>> {
    const revived: Record<string, Record<string, GuidePrimitive | Date>> = { ...responses };
    doc.sections.forEach((section) => {
      section.fields.forEach((field) => {
        const value = revived[section.id]?.[field.id];
        if (field.type === 'date' && typeof value === 'string' && value) {
          const parsed = new Date(value);
          // Skip a malformed stored string rather than patch an Invalid Date into the picker.
          if (!isNaN(parsed.getTime())) {
            revived[section.id] = { ...revived[section.id], [field.id]: parsed };
          }
        }
      });
    });
    return revived;
  }

  private buildForm(doc: CareCallGuideDoc): void {
    const group: Record<string, UntypedFormGroup> = {};
    doc.sections.forEach((section) => {
      const fields: Record<string, (GuidePrimitive | Date)[]> = {};
      section.fields.forEach((field) => {
        // 'info' and 'computed' fields are read-only display only — no control needed.
        if (field.type === 'info' || field.type === 'computed') {
          return;
        }
        fields[field.id] = [this.initialFieldValue(field)];
      });
      group[section.id] = this.fb.group(fields);
    });
    this.guideForm = this.fb.group(group);
  }

  /** Initial control value: empty for inputs, `false` for checkboxes. Patient context is
   * no longer prefilled into fields — it is rendered in the generated note header instead. */
  private initialFieldValue(field: GuideField): GuidePrimitive {
    return field.type === 'checkbox' ? false : '';
  }

  /**
   * Reads the §8.2 screening for this patient, OUTSIDE the guide-load chain.
   *
   * Deliberately not part of the `switchMap` above. That chain decides which fields exist, so the
   * form cannot render until it resolves; this decides what a banner says. Joining them would let a
   * slow eligibility read hold up the whole guide, and the MA would be staring at a spinner because
   * of a panel that is advisory. The service already collapses its own failures to `null`, so this
   * subscription has no error path to swallow.
   *
   * CHF ONLY, AND ONLY AT AN ONBOARDED CLINIC. §8.2 is about diuretic titration under the Emory
   * standing orders, so it is scoped exactly as the SOC guide fields are — `socScopeEnabled`, which
   * is the clinic toggle AND the caller being in the canary. Two reasons, and the first is the one
   * that matters: a clinic not running this protocol has no business being told its patient is
   * "excluded from Welby-driven diuretic titration", because it does not do Welby-driven diuretic
   * titration. The second is cost — without the gate this fires on every CHF call at all 40 clinics,
   * against an endpoint whose throttle is per caller and whose failure mode is a banner that
   * suppresses the option.
   *
   * PINNED TO THE PATIENT IT STARTED FOR, like every other async read in this component. Without the
   * re-check an MA who switches patients mid-read sees patient A's exclusions on patient B's call —
   * and this banner exists to be acted on, so showing the wrong one is worse than showing none.
   */
  private loadDiureticEligibility(): void {
    this.diureticEligibility = null;
    this.eligibilityResolved = false;
    if (this.resolvedDiagnosis !== 'chf' || !this.socScopeEnabled) {
      return;
    }
    const patientId = this.patientService.currentPatientServiceID;
    if (!patientId) {
      // RESOLVED, WITH NOTHING — not silent. Staying quiet here would be the one outcome this banner
      // exists to prevent: on a CHF call, at an onboarded clinic, for a canary MA, an absent patient
      // id would render exactly like a non-CHF call, and absence would read as permission.
      //
      // It is reachable, and the component already documents why: this runs during `ngOnInit`, and
      // `canWriteToActivePatientNote` carries a deliberate fail-open for "the guide loaded before the
      // patient id resolved". There is no retry — `loadDiureticEligibility` is called once — so a
      // patient that resolves a tick later would otherwise never get a banner at all.
      //
      // The scope guard above stays silent by contrast, and correctly: there the protocol does not
      // apply. Here it applies and could not be evaluated, which is a different thing and says so.
      this.eligibilityResolved = true;
      return;
    }
    this.clinicalEligibilityService
      .getChfDiureticEligibility(patientId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((view) => {
        if (this.patientService.currentPatientServiceID !== patientId) {
          return;
        }
        this.diureticEligibility = view;
        this.eligibilityResolved = true;
      });
  }

  /**
   * Post the §8.2 screening answers, if the MA gave a complete set on this call.
   *
   * THIRD STREAM IN THE SAME forkJoin, with its own catchError, exactly as the note and the
   * escalations are. Each owns its failure so one cannot abort the others: an attestation that fails
   * must not cost the MA their note, and a failed note must not lose a screening the clinician
   * completed.
   *
   * WHY IT RIDES ON "Generate Note" RATHER THAN FIRING AS THE BOXES ARE TICKED. The record decides
   * whether a dose escalation may be offered for the next ninety days, so it should be written when
   * the clinician has finished the call, not while they are still working through the questions —
   * a mid-form post would file a verdict off a half-answered block, and unticking afterwards would
   * not retract it.
   *
   * `of(null)` when there is nothing to say. The overwhelmingly common case is a patient with a
   * current verdict, whose questions never rendered.
   */
  private attestDiureticScreen$(): Observable<unknown> {
    // NCMs diligence the form themselves and raise nothing from it — same carve-out the escalation
    // walk applies. Their pass over the guide must not overwrite the MA's screening record.
    if (!this.diureticScreenDue || this.suppressEscalations) {
      return of(null);
    }
    const patientId = this.patientService.currentPatientServiceID;
    if (!patientId) {
      return of(null);
    }
    const attestation = buildDiureticAttestation(this.collectDiureticScreenValues());
    if (!attestation.payload) {
      // SILENT. The only reason there is no payload is an unfinished block, which is how most calls
      // end — warning about it would train the MA to ignore the one warning that matters, the failed
      // save below.
      return of(null);
    }
    return this.clinicalEligibilityService.attestChfDiureticEligibility(patientId, attestation.payload).pipe(
      tap((view) => {
        if (this.patientService.currentPatientServiceID !== patientId) {
          return;
        }
        // Refresh the banner from the RESPONSE rather than re-fetching: the API returns the
        // inference-applied view, so an ICD exclusion the clinician could not see is reflected
        // immediately instead of a stale "Not screened" sitting above a screen they just completed.
        this.diureticEligibility = view;
      }),
      catchError(() => {
        // PINNED TO THE PATIENT, exactly as the success path above is. Raised in review: without it
        // an MA who opens another chart while this POST is in flight sees "screening was not saved"
        // over patient B's record, about patient A — plausibly read as being about B, while A's
        // screening silently stays unrecorded. The file's own "PINNED TO THE PATIENT" convention
        // existed for this class of async-write-after-navigation risk and was applied on one branch
        // and not the other.
        if (this.patientService.currentPatientServiceID !== patientId) {
          return of(null);
        }
        // NOT silent, unlike the GET. The clinician answered the questions and believes the patient
        // is screened; leaving the record saying nobody asked, with no word to them, is the failure
        // this whole block exists to prevent.
        this.snackService.genericSnackBar(
          // No section number: this is read mid-call by an MA, to whom "§8.2" means nothing. The
          // exclusion questions are the ones they just answered, and naming them that is enough.
          'Diuretic screening was not saved — re-check the exclusion questions before any dose change',
          ['warn-snackbar'],
          8000,
        );
        return of(null);
      }),
    );
  }

  /**
   * The five §8.2 answers, keyed by field id, read from whichever section the seed put them in.
   *
   * FILTERED THROUGH `isFieldVisible`, like every other collector in this file and for the reason
   * `buildPromptResponses` documents: nothing clears a control when its parent stops matching, so a
   * field hidden mid-call still holds its old value. Collecting that would POST an answer to a
   * question the MA is no longer being asked — and this payload is a clinical attestation, so a
   * stale value here is an attested screen nobody performed.
   *
   * Raised in review. The `isFieldVisible` override added alongside this anticipates exactly this
   * ("a screening field may also carry a sibling rule, and both must pass") and the collector then
   * ignored it, which is the kind of gap that survives precisely because the comment reads correct.
   */
  private collectDiureticScreenValues(): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    (this.guide?.sections ?? []).forEach((section) => {
      (section.fields ?? []).forEach((field) => {
        if (field.screen_gate === DIURETIC_SCREEN_GATE && this.isFieldVisible(section, field)) {
          values[field.id] = this.controlValue(section.id, field.id);
        }
      });
    });
    return values;
  }

}
